import {
  createAsyncThunk,
  createSelector,
  createSlice,
  unwrapResult,
  type PayloadAction,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit';
import type { AppExtraArgument, AppState, AppStateListenerStart } from '@src/providers/AppStateProvider/types';
import { getSymbolAndMarketsNoUnifiedLiquidity } from '@src/utils/symbolAndMarketsNoUnifiedLiquidity';
import type { FieldData, MarketSecurityStatus } from '@talos/kyoko';
import {
  AllocationValueTypeEnum,
  BaseField,
  canCcyFutureOrPerp,
  DateField,
  DateTimeDurationPickerValueType,
  DecisionStatusEnum,
  EMPTY_ARRAY,
  ExpressedOutcomeEnum,
  Field,
  getExpirationByMarketByCurrencyIdentity,
  getGroup,
  getScaleFromIncrement,
  getStrikesByOptionKey,
  getTypedKeys,
  includesEnumValue,
  InstrumentCompositionEnum,
  isCFD,
  isDelta1Multileg,
  isFuture,
  isMultileg,
  isOption,
  isOptionStrategy,
  isPerpetualSwap,
  isSecurityQuantoFuture,
  isSpot,
  logger,
  ModeEnum,
  NumericField,
  OrderMarketStatusEnum,
  OrdTypeEnum,
  ParameterTypeEnum,
  parseDate,
  parseDuration,
  PricingMode,
  ProductTypeEnum,
  SecurityTypesUtils,
  SelectorField,
  shouldDisplayReasonabilityCheckForProduct,
  SideEnum,
  supportsBPSPricing,
  SyntheticProductTypeEnum,
  toBigWithDefault,
  UnifiedLiquidityEnum,
  Unit,
  type Allocation,
  type CareOrder,
  type ExecutionReport,
  type MarketAccount,
  type MarketDataStatistics,
  type Order,
  type OrderStrategy,
  type ProductGroup,
  type Security,
  type StringSelectItem,
} from '@talos/kyoko';
import Big from 'big.js';
import type { WritableDraft } from 'immer';
import { freeze, isDraft, original } from 'immer';
import { compact, debounce, isEqual, uniq } from 'lodash-es';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import { MULTILEG_MARKET_ACCOUNT, type LegParams } from '../../../providers/OMSContext.types';
import type { IOrderPreset } from '../../../providers/OrderPresetsContext';
import type { ResubmitOrderSource } from '../../../providers/orders.types';
import type { OrderStrategiesEnum } from '../../../tokens/orderStrategy';
import { DISABLED_REASONABILITY_CHECK_STRATEGIES, ParameterKeysEnum } from '../../../tokens/orderStrategy';
import type { Balance } from '../../../types';
import { isStrategySupportedBySecurity } from '../../../utils/security';
import { getDDHDefaultsID } from '../../DDH/utils';
import { getOrderMarketSelectorItems } from '../../MarketSelector/Orders/useOrderMarketSelectorItems';
import type {
  MarketSelectorItem,
  MarketSelectorPreset,
  MarketSelectorUnifiedLiquiditySelection,
  MarketSelectorValue,
} from '../../MarketSelector/types';
import { LegParamsField } from '../../MarketSelector/utils/LegParamsField';
import { OrderMarketsField } from '../../MarketSelector/utils/OrderMarketsField';
import {
  getSelectedMarketAccountsWithoutUnifiedLiquiditySymbols,
  getUnifiedLiquiditySelectionsForMarketAccounts,
} from '../../MarketSelector/utils/unifiedLiquidity';
import { allowMultipleAcctsPerCred } from '../../MarketSelector/utils/useAllowMultipleAcctsPerCred';
import { filterOrderPresets } from '../../OrderPresets/OrderPresetSearchSelect';
import { suspendTracking } from '../../PreTrade/redux/preTradeSlice';
import {
  getInitMarketsThunk,
  getMarketSecurityStatusesThunk,
  getMarketSelectorItemsListener,
  getMarketSelectorItemsThunk,
} from '../Common/marketSelectionSliceUtils';
import {
  isCareOrWorkingOrderParentType,
  startCareOrderParentListener,
  type OrderParentType,
} from '../NewRFQ/tradeCareOrder';
import { isStsDigitalOption } from '../NewRFQ/utils';
import {
  initialRealTimeSettingsState,
  setRealTimeOMSSettings,
  type OMSRealTimeSettingsState,
} from '../OMSRealTimeSettingsSlice';
import { initialRefDataState, setOMSSettings } from '../OMSReferenceDataSlice';
import { OMSSliceActions } from '../OMSSlice';
import { OMSView } from '../OMSView';
import { getScaleForPriceUnit } from '../SalesOrder/Strategies';
import {
  allocationsAreValid,
  disableAllocationFields,
  mapAllocationsToField,
  touchAllocations,
  updateSubAccountOrAllocationState,
  validateAllocations,
} from '../allocationUtils';
import { numberIsPositive } from '../commonFieldRules';
import type { OMSReferenceDataState } from '../types';
import { getAllowedCurrencies, getNormalizedOrderQty, getOrderCurrency } from '../useGetOrderCurrency';
import { getReduceFirstSupport } from '../useGetReduceFirstSupport';
import { getReduceOnlySupport } from '../useGetReduceOnlySupport';
import { getOrdType } from '../utils';
import {
  careOrderQuantityValidation,
  clipIntervalValidation,
  clipScheduleUnderfillEndTimeValidation,
  clipSizeValidation,
  endTimeValidation,
  greaterThanCumQtyValidation,
  isClipScheduleStrategy,
  notInPastValidation,
  optionPriceRangeValidation,
  predictionPriceRangeValidation,
  priceValidation,
  quantityValidation,
} from './FieldRules';
import { orderSliceApi, type MarginCostRequestParams } from './OrderSliceApi';
import { Future, Option, Perp, type MarginCostResponse, type OptionModelReferenceData } from './models';
import { resolveEndTimePrimingDecision } from './resolveEndTimePrimingDecision';
import type { OrderAdditionalDependencies, OrderFormState, OrderState, PrimeOMSParams } from './types';
import {
  floorOrderQuantityToMinIncrement,
  getDisplayedOrderPrice,
  getHedgingSecurities,
  getLegMarketAccountsFromOrder,
  getOrderMarketAccountsFromOrder,
  getOrderSliceParentType,
  getOrderSlicePosition,
  getPositionAmounts,
  initializeStrategyParams,
  isNewOrderSecurity,
  ORDERSLICE_POSITION_REQUEST_PARAMS,
} from './utils';
import { mapSyntheticProductTypeToLabel } from './utils/mappers';
import { selectMarginCost, selectMarginCostRequestParams } from './utils/marginCostSelectors';
import { startModifyOrderCardDataListener } from './utils/modifyOrderCardData';
import {
  makeSelectBuildOrderMarketSelectorItemsParams,
  selectBuildOrderMarketSelectorItemsParams,
} from './utils/selectors';

// STS Digital options require settlement type/currency selection before a symbol can be resolved.
// New Order does not yet support that UI, so exclude STS options entirely to avoid a half-baked state
// where sts_digital appears in the exchange dropdown but can never be completed.
function toOrderOptionRefData(referenceData: OMSReferenceDataState): OptionModelReferenceData {
  const options = referenceData.options.options.filter(opt => !isStsDigitalOption(opt));
  return {
    options: {
      options,
      optionSecurityBySymbol: new Map(options.map(option => [option.Symbol, option])),
      expirationByMarketByCurrencyIdentity: getExpirationByMarketByCurrencyIdentity(options),
      strikesByOptionKey: getStrikesByOptionKey(options),
    },
    markets: referenceData.markets,
    marketAccounts: referenceData.marketAccounts,
  };
}

/** Single entry point for building the order form's Option model, from a security or blank. */
function createOrderOptionModel(state: WritableDraft<OrderState>, security?: Security) {
  const optionModelRefData = toOrderOptionRefData(state.referenceData);
  state.form.option =
    security && SecurityTypesUtils.Option.isType(security)
      ? Option.createFromSecurity(optionModelRefData, security)
      : Option.createFromBlank(optionModelRefData);
}

export const getInitialState = (): OrderState => ({
  dependencies: {
    tradableSubAccounts: EMPTY_ARRAY,
  },
  realTimeSettings: initialRealTimeSettingsState,
  referenceData: initialRefDataState,
  form: {
    symbolField: new Field<Security>({ name: 'Symbol' }),
    sideField: new Field({ value: SideEnum.Buy }),
    strategyField: new SelectorField({ idProperty: 'Name', name: 'Strategy' }),
    orderPresetField: new SelectorField({
      idProperty: 'id',
      name: 'Preset',
      isRequired: false,
      placeholder: 'Select a preset',
    }),
    quantityField: new NumericField({ name: 'Quantity', scale: undefined }), // quantity validation fully managed by FieldRules.quantityValidation
    orderCurrencyField: new SelectorField(),
    commentsField: new Field({ name: 'Comments', isRequired: false }),
    stagedOrderField: new Field({ name: 'Staged', value: false, isRequired: false }),
    groupField: new Field({ name: 'Group', isRequired: false }),
    parentIDField: new Field({ name: 'Parent ID', isRequired: false }),
    parentType: undefined,
    assignToParentField: new Field({ name: 'Assign to Parent', isRequired: false }),
    priceField: new NumericField({ name: 'Price' }).validate(),
    priceModeField: new SelectorField<PricingMode>({ name: 'Price Ccy', value: PricingMode.Default }),
    parameters: {},
    allocationValueTypeField: new Field({ value: AllocationValueTypeEnum.Percentage }),
    allocations: EMPTY_ARRAY,
    orderMarketsField: new OrderMarketsField({ name: 'Market Account' }),
    unifiedLiquidityMarketSymbols: EMPTY_ARRAY,
    multilegTypeField: new SelectorField({ isRequired: false }),
    legParams: new LegParamsField({
      name: 'Leg Params',
      isRequired: false,
    }),
    ulLegParamsField: new LegParamsField({
      name: 'Unified Leg Params',
      isRequired: false,
    }),
    isDDHEnabled: new SelectorField({ name: 'Dynamic Delta Hedging', value: false }),
    ddhStrategyField: new SelectorField({ idProperty: 'Name', name: 'DDH Strategy', isRequired: false }),
    ddhSymbolField: new SelectorField({ idProperty: 'Symbol', name: 'DDH Symbol', isRequired: false }),
    ddhAggregationField: new Field({ name: 'DDH Aggregation', isRequired: false }),
    ddhUnifiedLiquidityField: new Field<UnifiedLiquidityEnum>({ name: 'DDH Unified Liquidity', isRequired: false }),
    option: undefined,
    perp: undefined,
    future: undefined,
    isPredictionClose: false,
  },
  initialized: false,
  isLoading: false,
  orderBeingModified: undefined,
  productGroup: 'Spot',
  marketDataStatistics: undefined,
});

function shouldOnlyPrimePriceForRePriming(state: WritableDraft<OrderState>, payload: PrimeOMSParams) {
  // Price-only re-priming is only safe when we are reapplying data for the same symbol and same parent.
  // A changed parent ID carries care-order assignment state that must go through the full priming path.
  // Close Position also needs a full prime so qty, currency, and routing replace the prior order.
  return (
    !payload.forceFullPrime &&
    !payload.isPredictionClose &&
    !state.form.isPredictionClose &&
    state.referenceData.settings.exclusivelyPrimePriceOnRePriming &&
    payload.symbol === state.form.symbolField.value?.Symbol &&
    payload.parent?.id === state.form.parentIDField.value
  );
}

/** Close Position locks Sell on the form; clearing the flag re-enables the side selector. */
function setPredictionCloseMode(state: WritableDraft<OrderState>, isPredictionClose: boolean) {
  state.form.isPredictionClose = isPredictionClose;
  state.form.sideField = state.form.sideField.setDisabled(isPredictionClose);
}

const updatePriceFieldVisibility = (state: WritableDraft<OrderState>, ordType: OrdTypeEnum) => {
  const isPriceFieldRequired = ordType !== OrdTypeEnum.Market;

  if (!isPriceFieldRequired) {
    state.form.priceField = state.form.priceField.updateValue(undefined);
  }

  state.form.priceField = state.form.priceField.setIsRequired(isPriceFieldRequired).setIsVisible(isPriceFieldRequired);
};

type CareOrderAssignmentState = Pick<CareOrder, 'Currency' | 'Group' | 'Side' | 'SubAccount' | 'Symbol'>;

// When a care order is assigned to the order form, we carry over certain properties and "lock" them in.
function applyCareOrderAssignmentState(
  state: WritableDraft<OrderState>,
  careOrder: CareOrderAssignmentState | undefined
) {
  const security = careOrder?.Symbol
    ? state.referenceData.securities.securitiesBySymbol.get(careOrder.Symbol)
    : undefined;
  if (security != null) {
    state.form.symbolField = state.form.symbolField.updateValue(security);
  }
  state.form.symbolField = state.form.symbolField.setDisabled(true);
  state.form.sideField = state.form.sideField.updateValue(careOrder?.Side).setDisabled(true);

  if (careOrder?.Currency != null) {
    state.form.orderCurrencyField = state.form.orderCurrencyField.updateValue(careOrder.Currency);
  }
  if (careOrder?.Group) {
    // care order group is used as the client's external ID for the order. We force that onto the order, too.
    state.form.groupField = state.form.groupField.updateValue(careOrder.Group).setDisabled(true);
  }
  if (careOrder?.SubAccount) {
    updateSubAccountOrAllocationState(state, { SubAccount: careOrder.SubAccount });
    disableAllocationFields(state, true);
  }
}

// (Mostly) just re-enables the fields that no longer are coupled to the care order's
function clearCareOrderAssignmentState(
  state: WritableDraft<OrderState>,
  careOrder: CareOrderAssignmentState | undefined
) {
  state.form.symbolField = state.form.symbolField.setDisabled(false);
  state.form.sideField = state.form.sideField.setDisabled(false);

  if (careOrder?.Group) {
    // Group is used as an external ID by clients for care orders in some cases, so we make sure to clear it when the care order is unassigned
    state.form.groupField = state.form.groupField.updateValue('').setDisabled(false);
  }
  if (careOrder?.SubAccount) {
    disableAllocationFields(state, false);
  }
}

const validateQuantity = (state: WritableDraft<OrderState>) => {
  const quantityRules = [
    numberIsPositive,
    quantityValidation,
    careOrderQuantityValidation,
    greaterThanCumQtyValidation,
  ];
  state.form.quantityField = state.form.quantityField.validate(quantityRules, state);
};

const validatePrice = (state: WritableDraft<OrderState>) => {
  // Even if pricing mode is IV or UnderlyingQuoteCurrency, price can still not be 0
  state.form.priceField = state.form.priceField.validate(
    [optionPriceRangeValidation, priceValidation, predictionPriceRangeValidation],
    state
  );
};

const initializeFieldsFromOrder = (
  state: WritableDraft<OrderState>,
  order: ExecutionReport,
  isModify = false,
  parentType?: OrderParentType
) => {
  const isStaged = order.DecisionStatus === DecisionStatusEnum.Staged;
  state.form.stagedOrderField = state.form.stagedOrderField.updateValue(isStaged);
  const security = state.referenceData.securities.securitiesBySymbol.get(order.Symbol);

  state.form.symbolField = state.form.symbolField.setDisabled(isModify).updateValue(security);

  handleSymbolChange(state, order.Symbol);

  state.form.sideField = state.form.sideField
    .setDisabled(isModify)
    .updateValue(order.Side === SideEnum.Buy ? SideEnum.Buy : SideEnum.Sell);
  state.form.strategyField = state.form.strategyField
    .setDisabled(isModify && !isStaged)
    .updateValueFromID(order.Strategy);
  state.form.quantityField = state.form.quantityField.updateValue(
    order.OrderQty && toBigWithDefault(order.OrderQty, 0).toFixed()
  );
  state.form.orderPresetField = state.form.orderPresetField.setDisabled(isModify).updateValue(undefined);

  // When contract rather than sending back empty string, back end sends back undefined - which is not the same
  state.form.orderCurrencyField = state.form.orderCurrencyField.setDisabled(isModify).updateValue(order.Currency || '');
  validateQuantity(state);

  // Always set pricing mode from order, even when undefined.
  // Backend represents "Cash/Default" pricing as undefined (not sent), while frontend uses PricingMode.Default.
  // handleSymbolChange (called above) sets pricing mode based on user display settings (e.g. BPS if enabled).
  // For modify/resubmit, we override that with the order's actual pricing mode to match what was originally placed.
  const pricingModeFromOrder = (order.PricingMode as unknown as PricingMode) ?? PricingMode.Default;
  updatePriceModeField(state, priceModeField => priceModeField.updateValue(pricingModeFromOrder).setDisabled(isModify));

  state.form.priceField = state.form.priceField.updateValue(getDisplayedOrderPrice(order), true);
  validatePrice(state);

  state.form.parameters = initializeStrategyParams({
    security: state.form.symbolField.value,
    strategy: state.form.strategyField.value,
    settings: state.referenceData.settings,
    isModify,
  });

  const ordType = getOrdType(
    state.referenceData.settings.showAllInPrices,
    state.form.symbolField.value,
    state.form.strategyField.value
  );
  updatePriceFieldVisibility(state, ordType);

  getTypedKeys(state.form.parameters).forEach(key => {
    const parameterValue = order.Parameters?.[key as keyof typeof order.Parameters];
    if (parameterValue) {
      let value = parameterValue;
      if (key === ParameterKeysEnum.UnifiedLiquidity && !isModify) {
        // On resubmit, fall back to Disabled if the symbol no longer has a UL token.
        // On modify, always preserve the original order's value — the token map may not
        // be populated yet (timing) or the symbol may lack a token (e.g. options), but
        // the order was validly placed with this setting and we must not override it.
        const token = state.referenceData.unifiedLiquidityTokens?.get(order.Symbol);
        value = token != null ? value : UnifiedLiquidityEnum.Disabled;
      }
      state.form.parameters[key] = state.form.parameters[key].updateValue(value, true).setTouched(true);
    }
  });

  const endTimePrimingDecision = resolveEndTimePrimingDecision({
    order,
    parameters: state.form.parameters,
    action: isModify ? 'modify' : 'resubmit',
  });

  // Prime EndTime from Duration when possible; otherwise for running modify flow use computed DateTime.
  if (endTimePrimingDecision.kind === 'duration') {
    state.form.parameters[ParameterKeysEnum.EndTime] = (state.form.parameters[ParameterKeysEnum.EndTime] as DateField)
      .updateValue({
        value: parseDuration(endTimePrimingDecision.duration),
        type: DateTimeDurationPickerValueType.Duration,
      })
      .setTouched(true);
  } else if (endTimePrimingDecision.kind === 'dateTime') {
    state.form.parameters[ParameterKeysEnum.EndTime] = state.form.parameters[ParameterKeysEnum.EndTime]
      .updateValue(endTimePrimingDecision.endTime, true)
      .setTouched(true);
  }

  if (isModify && state.form.parameters.StartTime) {
    // Allow modifying start time if WaitingForStartTime || Staged
    const allowEdit = includesEnumValue(
      [DecisionStatusEnum.WaitingForStartTime, DecisionStatusEnum.Staged],
      order.DecisionStatus
    );
    state.form.parameters.StartTime = state.form.parameters.StartTime.setDisabled(!allowEdit);
  }

  if (state.form.parameters.StartTime && state.form.parameters.EndTime) {
    state.form.parameters.EndTime = (state.form.parameters.EndTime as DateField).setRelativeDate({
      type: DateTimeDurationPickerValueType.DateTime,
      value: state.form.parameters.StartTime.value,
    });
  }
  if (order.SubAccount) {
    const allocations: Allocation[] = [
      {
        subAccount: order.SubAccount,
        value: '1',
      },
    ];
    state.form.allocations = mapAllocationsToField(allocations, AllocationValueTypeEnum.Percentage);
  }
  if (order.Allocation) {
    const allocations: Allocation[] = order.Allocation.Allocations.map(alloc => ({
      subAccount: alloc.SubAccount,
      value: alloc.Value || '1',
    }));
    state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(order.Allocation.ValueType);
    state.form.allocations = mapAllocationsToField(allocations, order.Allocation.ValueType);
  }
  if (isModify && state.form.parameters.ReduceOnly) {
    state.form.parameters.ReduceOnly = state.form.parameters.ReduceOnly.setDisabled(isModify);
  }

  state.form.commentsField = state.form.commentsField.updateValue(order.Comments);
  state.form.groupField = state.form.groupField.updateValue(order.Group);
  state.form.parentIDField = state.form.parentIDField.updateValue(order.ParentOrderID);
  state.form.parentType = parentType;
  state.form.assignToParentField = state.form.assignToParentField.updateValue(order.ParentOrderID != null);

  if (isMultileg(state.form.symbolField.value)) {
    const security = state.form.symbolField.value;
    state.form.legParams = LegParamsField.Create(security);
    state.form.multilegTypeField = state.form.multilegTypeField.setDisabled(isModify);

    const enableUnifiedLiquidity =
      state.form.parameters.UnifiedLiquidity?.value === UnifiedLiquidityEnum.Enabled &&
      state.referenceData.unifiedLiquidityTokens?.get(security.Symbol) != null;

    if (enableUnifiedLiquidity) {
      state.form.ulLegParamsField = LegParamsField.CreateUL(
        security,
        state.referenceData.unifiedLiquidityTokens
      ).setIsRequired(true);
    }
  }

  // Dynamic Delta Hedging, DDH
  if (order.Parameters && order.Parameters?.DynamicDeltaHedgeStrategy) {
    const strategy = state.form.strategyField.value;

    if (strategy?.hasParameterDrivenDDH) {
      // Parameter-driven DDH path: DynamicDeltaHedgingEnabled is a UI-only toggle never emitted to the backend,
      // and child params (Symbol, Aggregation, UnifiedLiquidity) are nested under the payloadKey rather than
      // at the top level of Parameters. Hydrate them here so that re-submitting a modify does not silently drop
      // DDH settings.
      const ddhStrategyParams = order.Parameters?.DynamicDeltaHedgeStrategyParams as Record<string, string> | undefined;
      if (ddhStrategyParams) {
        if (state.form.parameters['DynamicDeltaHedgingEnabled']) {
          state.form.parameters['DynamicDeltaHedgingEnabled'] = state.form.parameters['DynamicDeltaHedgingEnabled']
            .updateValue(ModeEnum.Enabled, true)
            .setTouched(true);
        }
        // Hydrate child params (HedgeStrategy, Symbol, Aggregation, UnifiedLiquidity) from the nested payload.
        // HedgeStrategy is emitted at the top level of Parameters (not nested), so read it from order.Parameters.
        // Symbol, Aggregation, and UnifiedLiquidity are nested under the payloadKey.
        strategy.Parameters.filter(p => p.isPrimary && p.payloadKey)
          .flatMap(primary => strategy.getSectionParameters(primary.section!).filter(p => !p.isPrimary))
          .forEach(childParam => {
            const field = state.form.parameters[childParam.Name];
            if (!field) {
              return;
            }

            // Step 1: locate the raw string from the correct source
            const rawValue =
              childParam.Type === ParameterTypeEnum.HedgeStrategy
                ? (order.Parameters?.[childParam.Name as keyof typeof order.Parameters] as string | undefined)
                : childParam.Type === ParameterTypeEnum.UnifiedLiquidity
                  ? ddhStrategyParams[ParameterTypeEnum.UnifiedLiquidity] // stored under type name, not param name
                  : ddhStrategyParams[childParam.Name];

            if (!rawValue) {
              return;
            }

            // Step 2: resolve typed value (secondary lookup where needed)
            const typedValue =
              childParam.Type === ParameterTypeEnum.HedgeStrategy
                ? state.referenceData.strategies.ddhStrategiesList.find(s => s.Name === rawValue)
                : childParam.Type === ParameterTypeEnum.Symbol
                  ? state.referenceData.securities.securitiesBySymbol.get(rawValue)
                  : rawValue;

            if (typedValue != null) {
              state.form.parameters[childParam.Name] = field.updateValue(typedValue, true).setTouched(true);
            }
          });
      }
    } else {
      // Legacy DDH path: used when the current strategy does NOT have DynamicDeltaHedgingEnabled as a parameter.
      state.form.isDDHEnabled = state.form.isDDHEnabled.updateValue(true);
      const ddhStrategyParams = order.Parameters?.DynamicDeltaHedgeStrategyParams as Record<string, string> | undefined;
      const ddhSymbol = ddhStrategyParams?.['DynamicDeltaHedgeSymbol'];
      const ddhAggregation = ddhStrategyParams?.['DynamicDeltaHedgeAggregation'];
      const ddhStrategy = order.Parameters.DynamicDeltaHedgeStrategy;
      if (ddhSymbol) {
        state.form.ddhSymbolField = state.form.ddhSymbolField.updateValue(
          state.referenceData.securities.securitiesBySymbol.get(ddhSymbol)
        );
      }
      if (ddhStrategy) {
        state.form.ddhStrategyField = state.form.ddhStrategyField.updateValue(
          state.referenceData.strategies.ddhStrategiesList.find(s => s.Name === ddhStrategy)
        );
      }
      if (ddhAggregation) {
        state.form.ddhAggregationField = state.form.ddhAggregationField.updateValue(ddhAggregation);
      }
      const ddhUnifiedLiquidity = ddhStrategyParams?.['UnifiedLiquidity'] as UnifiedLiquidityEnum | undefined;
      if (ddhUnifiedLiquidity) {
        state.form.ddhUnifiedLiquidityField = state.form.ddhUnifiedLiquidityField.updateValue(ddhUnifiedLiquidity);
      }
    }
  }
  state.form.isDDHEnabled = state.form.isDDHEnabled.setDisabled(isModify);
  state.form.ddhSymbolField = state.form.ddhSymbolField.setDisabled(isModify);
  state.form.ddhStrategyField = state.form.ddhStrategyField.setDisabled(isModify);
  state.form.ddhAggregationField = state.form.ddhAggregationField.setDisabled(isModify);
  state.form.ddhUnifiedLiquidityField = state.form.ddhUnifiedLiquidityField.setDisabled(isModify);

  hydratePredictionFieldsFromOrder(state, order);
};

// If we don't use original, iterating over a large collection of securities is slow due to immer's draft proxy creation.
// see https://immerjs.github.io/immer/array-methods
// We guard with isDraft because original() throws on non-drafts (e.g. frozen payloads assigned via state.referenceData = action.payload).
const getRawSecuritiesList = (state: WritableDraft<OrderState>): Security[] => {
  const securitiesList = state.referenceData.securities.securitiesList;
  return isDraft(securitiesList) ? (original(securitiesList) ?? securitiesList) : securitiesList;
};

const populateDropdownsFromRefData = (state: WritableDraft<OrderState>) => {
  const { strategies, orderPresets } = state.referenceData;

  state.form.strategyField = state.form.strategyField.updateAvailableItems(strategies.strategiesList);
  state.form.ddhStrategyField = state.form.ddhStrategyField.updateAvailableItems(strategies.ddhStrategiesList);
  state.form.orderPresetField = state.form.orderPresetField.updateAvailableItems(orderPresets.orderPresetsList, {
    preventDefaultToValue: true,
  });

  const rawSecuritiesList = getRawSecuritiesList(state);

  const uniqueSyntheticProductTypes = new Set(
    // This should not happen but just in case we have SyntheticProductTypes not covered from the below then we are still
    // able to populate the dropdown correctly
    rawSecuritiesList.map(s => s.MultilegDetails?.SyntheticProductType || '').filter(x => !!x)
  );
  // we always want Delta 1 and Synthetic Cross no matter what
  uniqueSyntheticProductTypes.add(SyntheticProductTypeEnum.Delta1Spread);
  uniqueSyntheticProductTypes.add(SyntheticProductTypeEnum.Cross);
  if (state.referenceData.settings.enableOptionTrading) {
    uniqueSyntheticProductTypes.add(SyntheticProductTypeEnum.CallSpread);
    uniqueSyntheticProductTypes.add(SyntheticProductTypeEnum.PutSpread);
    uniqueSyntheticProductTypes.add(SyntheticProductTypeEnum.CallCalendarSpread);
    uniqueSyntheticProductTypes.add(SyntheticProductTypeEnum.PutCalendarSpread);
  }

  const syntheticProductTypeSelectItems: StringSelectItem[] = Array.from(uniqueSyntheticProductTypes.values()).map(
    v => ({
      label: mapSyntheticProductTypeToLabel(v as SyntheticProductTypeEnum),
      value: v,
    })
  );

  state.form.multilegTypeField = state.form.multilegTypeField
    .updateAvailableItems(syntheticProductTypeSelectItems)
    .updateValue(syntheticProductTypeSelectItems[0]);

  // Probably wouldn't happen in Principal but just in case as it has been observed in sales order
  if (rawSecuritiesList.length === 1) {
    handleSymbolChange(state, rawSecuritiesList[0]?.Symbol);
  }
};

const handleSymbolChange = (state: WritableDraft<OrderState>, symbol?: string) => {
  state.form.orderMarketsField = getInitialState().form.orderMarketsField;
  state.form.unifiedLiquidityMarketSymbols = [];
  state.form.predictionOutcome = undefined;
  if (state.form.isPredictionClose) {
    setPredictionCloseMode(state, false);
  }

  state.marketDataStatistics = undefined;

  const security = symbol ? state.referenceData.securities.securitiesBySymbol.get(symbol) : undefined;
  if (!security || !isNewOrderSecurity(security)) {
    state.form.symbolField = state.form.symbolField.updateValue(undefined);
    state.form.priceField = getInitialState().form.priceField;
    return;
  }

  const rawSecuritiesList = getRawSecuritiesList(state);

  state.form.ddhSymbolField = state.form.ddhSymbolField.updateAvailableItems(
    getHedgingSecurities(security, rawSecuritiesList)
  );

  state.form.orderPresetField = state.form.orderPresetField.updateAvailableItems(
    filterOrderPresets(state.referenceData.orderPresets.orderPresetsList, {
      security,
      strategies: state.referenceData.strategies.strategiesList,
    })
  );

  state.form.orderPresetField = state.form.orderPresetField.updateValue(undefined, true);

  const group = getGroup(security);
  state.productGroup = group;

  const hasSymbolChanged = state.form.symbolField.value ? state.form.symbolField.value.Symbol !== symbol : false;
  if (hasSymbolChanged) {
    state.form.priceField = getInitialState().form.priceField;
  }

  state.form.symbolField = state.form.symbolField.updateValue(security);

  // Strategy
  const filteredStrategies = state.referenceData.strategies.strategiesList.filter(strat =>
    isStrategySupportedBySecurity(strat, security)
  );
  const selectedStrategy = state.form.strategyField.value;
  // Automatically select a strategy if the one that was selected is not supported for the pair
  const nextStrategy =
    filteredStrategies.find(s => s.Name === selectedStrategy?.Name) ||
    filteredStrategies.find(
      s =>
        s.Name ===
        (isMultileg(security)
          ? state.referenceData.settings.defaultMultilegOrderStrategy
          : state.referenceData.settings.defaultOrderStrategy)
    ) ||
    filteredStrategies.find(s => s.Name === state.referenceData.settings.lastStrategyUsed) ||
    filteredStrategies[0];

  state.form.strategyField = state.form.strategyField
    .updateAvailableItems(filteredStrategies)
    .updateValue(nextStrategy);

  updateAvailableCurrencies(state);
  setInitialOrderCcy(state);

  state.form.quantityField = state.form.quantityField.updateValue(
    getNormalizedOrderQty({
      security,
      orderCurrency: state.form.orderCurrencyField.value,
      nonSpotDelta1DefaultQty: state.referenceData.settings.nonSpotDelta1DefaultQty,
      securitiesBySymbol: state.referenceData.securities.securitiesBySymbol,
    }),
    true
  );
  validateQuantity(state);

  updateLimitPriceCurrencies(state);

  syncPricingModeWithDisplaySetting(state);

  state.form.parameters = initializeStrategyParams({
    security: state.form.symbolField.value,
    strategy: state.form.strategyField.value,
    settings: state.referenceData.settings,
    isModify: !!state.orderBeingModified,
  });

  const ordType = getOrdType(
    state.referenceData.settings.showAllInPrices,
    state.form.symbolField.value,
    state.form.strategyField.value
  );
  updatePriceFieldVisibility(state, ordType);

  if (isMultileg(security)) {
    // The legParams inside the multileg encapsulate each leg's market
    // We don't display the regular market selector for multi legs either
    state.form.orderMarketsField = state.form.orderMarketsField.setIsRequired(false);
    state.form.legParams = LegParamsField.Create(security).setIsRequired(true);

    const multilegTypeValue = state.form.multilegTypeField.availableItems.find(
      item => item.value === security.MultilegDetails.SyntheticProductType
    );
    state.form.multilegTypeField = state.form.multilegTypeField.updateValue(multilegTypeValue);
  } else {
    state.form.orderMarketsField = state.form.orderMarketsField.setIsRequired(true);
    state.form.legParams = getInitialState().form.legParams;
    state.form.ulLegParamsField = getInitialState().form.ulLegParamsField;
  }

  if (group === 'Option' && SecurityTypesUtils.Option.isType(security)) {
    createOrderOptionModel(state, security);
  }
  if (group === 'Perp' && SecurityTypesUtils.Perp.isType(security)) {
    state.form.perp = Perp.createFromSecurity(state.referenceData, security);
  }
  if (group === 'Future' && SecurityTypesUtils.Future.isType(security)) {
    state.form.future = Future.createFromSecurity(state.referenceData, security);
  }
};

/** Defaults prediction orders to the Yes outcome when no outcome was explicitly selected. */
const deriveSelectionIfPredictionMarket = (state: WritableDraft<OrderState>) => {
  if (!SecurityTypesUtils.PredictionOutcome.isProductType(state.form.symbolField.value)) {
    return;
  }
  if (state.form.predictionOutcome == null) {
    state.form.predictionOutcome = ExpressedOutcomeEnum.Yes;
  }
  // Close Position primes a Sell of the held outcome; every other prediction order is a Buy.
  if (!state.form.isPredictionClose) {
    state.form.sideField = state.form.sideField.updateValue(SideEnum.Buy);
  }
};

const hydratePredictionFieldsFromOrder = (state: WritableDraft<OrderState>, order: ExecutionReport) => {
  if (!SecurityTypesUtils.PredictionOutcome.isProductType(state.form.symbolField.value)) {
    return;
  }

  const expressedOutcome = order.ExpressedOutcome;
  state.form.predictionOutcome = expressedOutcome ?? ExpressedOutcomeEnum.Yes;

  // New-model orders carry ExpressedOutcome. Close Position is a Sell of that outcome;
  // opening orders are a Buy. Legacy orders without ExpressedOutcome stay Buy.
  const isClose = expressedOutcome != null && order.Side === SideEnum.Sell;
  state.form.isPredictionClose = isClose;
  state.form.sideField = state.form.sideField.updateValue(isClose ? SideEnum.Sell : SideEnum.Buy);
  if (isClose) {
    state.form.sideField = state.form.sideField.setDisabled(true);
  }

  if (order.Price) {
    state.form.priceField = state.form.priceField.updateValue(order.Price, true);
    validatePrice(state);
  }
};

const setInitialOrderCcy = (state: WritableDraft<OrderState>) => {
  const security = state.form.symbolField.value;
  const shouldSetInitialOrderCurrency =
    SecurityTypesUtils.PredictionOutcome.isProductType(security) ||
    state.referenceData.settings.enableDerivativeContractDefault !== undefined;

  if (!shouldSetInitialOrderCurrency) {
    return;
  }

  // When priming use value in PrimeParams, enableDerivativeContractDefault is undefined on startup
  const initialOrderCurrency = getOrderCurrency({
    security,
    securitiesBySymbol: state.referenceData.securities.securitiesBySymbol,
    strategiesByName: state.referenceData.strategies.strategiesByName,
    enableDerivativeContractDefault: state.referenceData.settings.enableDerivativeContractDefault,
    nonSpotDelta1DefaultQty: state.referenceData.settings.nonSpotDelta1DefaultQty,
    orderSide: state.form.sideField.value,
    strategy: state.form.strategyField.value?.Name,
  });
  state.form.orderCurrencyField = state.form.orderCurrencyField.updateValue(initialOrderCurrency, true);
};

/**
 * When the "Default New Order/RFQ to Counter Currency" setting is enabled, switches the order
 * currency to the quote (counter) currency after the natural currency logic has already run.
 * Qty is cleared only when the currency actually changes (the ONE place qty is cleared here).
 * When the quote currency is already selected (e.g. inverse perp defaults to USD), we keep
 * qty as-is so the caller's intended qty is preserved.
 */
const applyCounterCurrencyDefault = (state: WritableDraft<OrderState>) => {
  if (!state.referenceData.settings.defaultNewOrderToCounterCurrency) {
    return;
  }
  const security = state.form.symbolField.value;
  const quoteCurrency = security?.QuoteCurrency;
  if (!quoteCurrency) {
    return;
  }
  if (!state.form.orderCurrencyField.availableItems.includes(quoteCurrency)) {
    return; // assuming available
  }
  if (state.form.orderCurrencyField.value === quoteCurrency) {
    return; // already counter → keep qty (e.g. inverse perp)
  }
  state.form.orderCurrencyField = state.form.orderCurrencyField.updateValue(quoteCurrency);
  state.form.quantityField = state.form.quantityField.updateValue(undefined, true);
};

const updateAvailableCurrencies = (state: WritableDraft<OrderState>) => {
  const allowedCurrencies = getAllowedCurrencies({
    security: state.form.symbolField.value,
    securitiesBySymbol: state.referenceData.securities.securitiesBySymbol,
    strategiesByName: state.referenceData.strategies.strategiesByName,
    orderSide: state.form.sideField.value,
    strategy: state.form.strategyField.value?.Name as OrderStrategiesEnum,
    disableCrossCurrencyQty: state.referenceData.settings.disableCrossCurrencyQty,
  });
  state.form.orderCurrencyField = state.form.orderCurrencyField.updateAvailableItems(allowedCurrencies);
};

/**
 * Syncs pricingMode from display settings (Delta-1 BPS toggle) into order form.
 * Only applies if security supports BPS pricing mode.
 *
 * Applies the display setting (synced to OMS settings):
 * - Setting disabled (showDelta1BPS = false): Sets to Default (Cash)
 * - Setting enabled (showDelta1BPS = true): Sets to BPS
 *
 * Display setting always overrides form toggle when it changes.
 */
const syncPricingModeWithDisplaySetting = (state: WritableDraft<OrderState>) => {
  const security = state.form.symbolField.value;
  const showDelta1BPS = state.realTimeSettings.showDelta1BPS;

  // Do not override pricing mode while modifying an existing order or if the field is disabled.
  if (state.orderBeingModified || state.form.priceModeField.isDisabled) {
    return;
  }

  // Only apply if security supports BPS
  if (!supportsBPSPricing(security)) {
    return;
  }

  // Apply toggle state: disabled = Cash (Default), enabled = BPS
  const nextPricingMode = showDelta1BPS ? PricingMode.BPS : PricingMode.Default;
  const nextPricingModeIsAvailable = state.form.priceModeField.availableItems.includes(nextPricingMode);
  const isCurrentPricingMode = state.form.priceModeField.value === nextPricingMode;

  if (nextPricingModeIsAvailable && !isCurrentPricingMode) {
    updatePriceModeField(state, priceModeField => priceModeField.updateValue(nextPricingMode));
    // Clear the price since we are changing pricing mode
    state.form.priceField = state.form.priceField.updateValue(undefined);
  }
};

const updateLimitPriceCurrencies = (state: WritableDraft<OrderState>) => {
  const security = state.form.symbolField.value;
  const strategy = state.form.strategyField.value;
  const supportedStrategiesForPricingModes = state.referenceData.settings.supportedStrategiesForPricingModes;

  if (!security || !strategy) {
    return;
  }

  const isStrategySupportedForPricingModes = supportedStrategiesForPricingModes.includes(strategy.Name);
  const supportedModes: PricingMode[] = [PricingMode.Default];

  if (security?.SupportedPricingModes?.ImpliedVolatility && isStrategySupportedForPricingModes) {
    supportedModes.push(PricingMode.ImpliedVolatility); // %
  }

  if (security?.SupportedPricingModes?.UnderlyingQuoteCurrency && isStrategySupportedForPricingModes) {
    supportedModes.push(PricingMode.UnderlyingQuoteCurrency); // USD
  }

  // BPS pricing mode supports all strategies, unlike other pricing modes which are restricted to supportedStrategiesForPricingModes
  if (security?.SupportedPricingModes?.BPS) {
    supportedModes.push(PricingMode.BPS);
  }

  const previousValue = state.form.priceModeField.value;
  updatePriceModeField(state, priceModeField => priceModeField.updateAvailableItems(supportedModes));
  const newValue = state.form.priceModeField.value ?? PricingMode.Default;

  if (previousValue !== newValue) {
    state.form.priceField = state.form.priceField.updateValue(undefined, true);
  }

  validatePrice(state);
};

/**
 * Updates the scale of a related parameter when a Units parameter changes.
 *
 * When a Units parameter (e.g., PriceUnits) changes, this function finds the parameter
 * that references it via its unitsParam metadata (e.g., Price, PriceOffset) and updates
 * that parameter's scale based on the new unit value and the security's minimum price increment.
 *
 * @param state - The writable draft of the order state
 * @param key - The name of the parameter that changed
 * @param value - The new value of the parameter
 */
const updateRelatedParameterScale = (state: WritableDraft<OrderState>, key: string, value: any) => {
  const strategy = state.form.strategyField.value;
  const security = state.form.symbolField.value;
  if (!strategy) {
    return;
  }
  const changedParameter = strategy.Parameters.find(p => p.Name === key);
  if (changedParameter?.Type === ParameterTypeEnum.Units) {
    // Find the parameter that references this Units param via its unitsParam metadata
    const relatedParam = strategy.Parameters.find(p => p.unitsParam === key);
    const relatedParamField = relatedParam ? state.form.parameters[relatedParam.Name] : undefined;
    if (relatedParam && relatedParamField instanceof NumericField) {
      // hasUnitsParam is true since we're in the Units parameter change handler
      const newScale = getScaleForPriceUnit(value, security?.MinPriceIncrement, true);
      state.form.parameters[relatedParam.Name] = relatedParamField.updateScale(newScale);
    }
  }
};

const updateLimitPriceScale = (state: WritableDraft<OrderState>) => {
  const security = state.form.symbolField.value;

  switch (state.form.priceModeField.value) {
    case PricingMode.ImpliedVolatility:
      // https://talostrading.atlassian.net/browse/UI-2809 hard coded 11 decimal place requirement
      state.form.priceField = state.form.priceField.updateScale(11).updateUnit(Unit.Percent);
      break;
    case PricingMode.UnderlyingQuoteCurrency:
      state.form.priceField = state.form.priceField
        .updateScale(getScaleFromIncrement(state.dependencies.USD?.MinIncrement) || 2)
        .updateUnit(Unit.Decimal);
      break;
    case PricingMode.BPS:
      // Backend stores BPS as absolute values (1 = 1 BPS), not decimals (0.0001 = 1 BPS)
      // So we use Unit.Decimal with 2 decimal places for BPS values like 1.50, 2.25, etc.
      state.form.priceField = state.form.priceField.updateScale(2).updateUnit(Unit.Decimal);
      break;
    case PricingMode.Default:
    default:
      state.form.priceField = state.form.priceField
        .updateScale(getScaleFromIncrement(security?.MinPriceIncrement))
        .updateUnit(Unit.Decimal);
      break;
  }
};

const updatePriceModeField = (
  state: WritableDraft<OrderState>,
  update: (priceModeField: WritableDraft<SelectorField<PricingMode>>) => SelectorField<PricingMode>
) => {
  state.form.priceModeField = update(state.form.priceModeField);
  // Any price mode value or available-items change can alter the active mode, so keep the price field unit/scale in sync.
  updateLimitPriceScale(state);
};

const handleOrderPresetChange = (
  state: WritableDraft<OrderState>,
  preset?: IOrderPreset,
  changeComingFrom?: PrimeOMSParams['primeFrom']
) => {
  if (preset == null) {
    state.form.orderPresetField = state.form.orderPresetField.updateValue(undefined);
    return;
  }
  state.form.orderPresetField = state.form.orderPresetField.updateValue(preset);
  // If the change is coming from a market card, we do not accept the preset.aggregation.
  const usePassedAggregation = preset.aggregation != null && changeComingFrom !== 'MarketCard';
  if (usePassedAggregation) {
    // If there is an aggregation on the preset, this means that we reset the orderMarketsField. The containing thunk will then initialize this to that aggregation.
    state.form.orderMarketsField = getInitialState().form.orderMarketsField;
  }
  handleStrategyChange(state, preset.strategy, true);
  for (const { key, value } of preset.parameters) {
    handleStrategyParamChange(state, { key, value }, true);
  }

  if (preset.allocations) {
    // If the user has disabled the usage of allocations (>1 sub accounts on an order), then we ignore the presets which have >1 sub acc on them.
    if (preset.allocations.length > 1 && !state.referenceData.settings.useTradeAllocations) {
      state.form.allocations = getInitialState().form.allocations;
    } else {
      const tradableSubAccounts = state.dependencies.tradableSubAccounts.map(subAccount => subAccount.Name);
      const allocations = preset.allocations.filter(allocation => tradableSubAccounts.includes(allocation.subAccount));
      state.form.allocations = mapAllocationsToField(allocations, AllocationValueTypeEnum.Percentage);
      validateAllocations(state);
    }
  }
};

const handleStrategyParamChange = (
  state: WritableDraft<OrderState>,
  { key, value }: { key: string; value?: any },
  isSystemOverride = false
) => {
  const parameterField = state.form.parameters[key];
  if (parameterField) {
    state.form.parameters[key] = parameterField.updateValue(value, isSystemOverride);
    if (key === ParameterKeysEnum.StartTime && state.form.parameters.EndTime) {
      state.form.parameters.EndTime = (state.form.parameters.EndTime as DateField).setRelativeDate(value);
    }

    if (key === ParameterKeysEnum.UnifiedLiquidity) {
      if (value === UnifiedLiquidityEnum.Enabled) {
        const security = state.form.symbolField.value;
        state.form.unifiedLiquidityMarketSymbols = getOrderUnifiedLiquidityMarketSymbols(state);
        if (isMultileg(security)) {
          state.form.ulLegParamsField = LegParamsField.CreateUL(
            security,
            state.referenceData.unifiedLiquidityTokens
          ).setIsRequired(true);
        }
      } else {
        // UL disabled
        state.form.unifiedLiquidityMarketSymbols = [];
        state.form.ulLegParamsField = getInitialState().form.ulLegParamsField;
      }
    }

    // When a Units parameter changes, update the scale of the related parameter (e.g., Price, PriceOffset)
    updateRelatedParameterScale(state, key, value);

    validateStrategyParams(state);
  }
};

function getOrderUnifiedLiquidityMarketSymbols(
  state: WritableDraft<OrderState>
): MarketSelectorUnifiedLiquiditySelection[] {
  const selectedMarketAccounts = state.form.orderMarketsField.selections.filter(selection =>
    state.referenceData.marketAccounts.marketAccountsByName.has(selection)
  );
  return getUnifiedLiquiditySelectionsForMarketAccounts({
    symbol: state.form.symbolField.value?.Symbol,
    selectedMarketAccounts,
    marketAccountsByName: state.referenceData.marketAccounts.marketAccountsByName,
    securitiesBySymbol: state.referenceData.securities.securitiesBySymbol,
    unifiedLiquidityTokenBySymbol: state.referenceData.unifiedLiquidityTokens,
  });
}

function getOrderUnifiedLiquiditySelectionsFromOrder(
  order: Pick<ResubmitOrderSource, 'Markets'>,
  selectedMarketAccounts: string[],
  fallbackSymbol: string
): MarketSelectorUnifiedLiquiditySelection[] {
  const selectedMarketAccountsByName = new Set(selectedMarketAccounts);
  const selectionsByKey = new Map<string, MarketSelectorUnifiedLiquiditySelection>();

  for (const market of order.Markets) {
    if (!market.MarketAccount || !selectedMarketAccountsByName.has(market.MarketAccount)) {
      continue;
    }

    if (market.MarketStatus === OrderMarketStatusEnum.Disabled) {
      continue;
    }

    const symbol = market.Symbol ?? fallbackSymbol;
    const key = `${market.MarketAccount}|${symbol}`;
    selectionsByKey.set(key, {
      marketAccount: market.MarketAccount,
      symbol,
    });
  }

  return Array.from(selectionsByKey.values());
}

const handleStrategyChange = (state: WritableDraft<OrderState>, strategyName?: string, isSystemOverride = false) => {
  const strategy = strategyName && state.referenceData.strategies.strategiesByName.get(strategyName);
  if (!strategy) {
    return;
  }

  state.form.strategyField = state.form.strategyField.updateValue(strategy, isSystemOverride);

  const newParameters = initializeStrategyParams({
    security: state.form.symbolField.value,
    strategy,
    settings: state.referenceData.settings,
    isModify: !!state.orderBeingModified,
  });

  // When switching strategies we carry over the previous parameter values if it also exists in the new strategy
  Object.keys(newParameters).forEach(paramName => {
    const existingParamField = state.form.parameters[paramName];
    if (existingParamField && existingParamField.isTouched) {
      // Different strategies might have same parameter with same Name but different DisplayName
      // So only safe to carry over the value, not the entire Field object
      if (existingParamField instanceof DateField && existingParamField.dateTimeDuration) {
        // special case with DateTime field duration, since value is an absolute point in time, whereas duration is relative
        // to the point when we actually submit the order. so if there is a duration we want to carry over the duration
        newParameters[paramName] = newParameters[paramName].updateValue(
          existingParamField.dateTimeDuration,
          isSystemOverride
        );
      } else if (newParameters[paramName] instanceof SelectorField) {
        // For enum parameters not only we need to ensure it exists in the new strategy but the previous enum value is also valid
        const enumValueExists = (newParameters[paramName] as SelectorField<number>).availableItems.includes(
          existingParamField.value
        );
        if (enumValueExists) {
          newParameters[paramName] = newParameters[paramName].updateValue(existingParamField.value, isSystemOverride);
        }
      } else {
        newParameters[paramName] = newParameters[paramName].updateValue(existingParamField.value, isSystemOverride);
      }
    }
  });

  state.form.parameters = newParameters;

  // After carrying over parameters, sync EndTime's relativeDate to StartTime so that
  // duration-based EndTimes resolve relative to StartTime, not "now".
  if (state.form.parameters.StartTime && state.form.parameters.EndTime) {
    const startTimeField = state.form.parameters.StartTime as DateField;
    state.form.parameters.EndTime = (state.form.parameters.EndTime as DateField).setRelativeDate(
      startTimeField.dateTimeDuration ??
        (startTimeField.value != null
          ? { type: DateTimeDurationPickerValueType.DateTime, value: parseDate(startTimeField.value) }
          : undefined)
    );
  }

  if (state.form.parameters.UnifiedLiquidity == null) {
    // If UL is ever removed upon the strategy changing, then we must reset/remove any ulLegParamsField that was up and running before.
    state.form.ulLegParamsField = getInitialState().form.ulLegParamsField;
  }

  const ordType = getOrdType(state.referenceData.settings.showAllInPrices, state.form.symbolField.value, strategy);
  updatePriceFieldVisibility(state, ordType);
  updateAvailableCurrencies(state);
  updateLimitPriceCurrencies(state);
};

function applyProductGroupFilter(state: WritableDraft<OrderState>, productGroup: ProductGroup | undefined) {
  state.productGroup = productGroup;
  if (productGroup === 'Option') {
    createOrderOptionModel(state);
  }
  if (productGroup === 'Perp') {
    state.form.perp = Perp.createFromBlank(state.referenceData);
  }
  if (productGroup === 'Future') {
    state.form.future = Future.createFromBlank(state.referenceData);
  }
  state.form.symbolField = state.form.symbolField.updateValue(undefined);
}

function restoreLastProductGroupUsed(state: WritableDraft<OrderState>) {
  if (state.form.symbolField.value != null || state.orderBeingModified != null) {
    return;
  }

  const lastProductGroupUsed = state.referenceData.settings.lastProductGroupUsed;
  if (!lastProductGroupUsed || lastProductGroupUsed === state.productGroup) {
    return;
  }

  applyProductGroupFilter(state, lastProductGroupUsed);
}

/**
 Unlike resetState which is called when we close the order form and reset every state property back to default,
 clean only resets part of the state. e.g. needed for when we go from modifying an order and midway deciding to
 resubmitting another - so we still need to keep certain state (settings/refData) but clean enough to
 transition into another order workflow essentially.
 Since priming does not guarantee full payload, i.e. sometimes payload includes subAccount, strategy and sometimes it does not
 which means we need to keep hold of existing value; in the event the next event is of being modify/resubmit
 then the allocations/subAccount/strategies etc will definitely exist and overwrite it; otherwise we keep current selection
 */
const cleanFormState = (state: WritableDraft<OrderState>) => {
  const prevAllocations = state.form.allocations;
  const prevStrategy = state.form.strategyField;

  state.form = getInitialState().form;

  state.form.allocations = prevAllocations;
  state.form.strategyField = prevStrategy.setDisabled(false);
  state.orderBeingModified = undefined;
  state.currentCareOrder = undefined;

  state.productGroup = 'Spot';
  return state;
};

const validateStrategyParams = (state: WritableDraft<OrderState>) => {
  Object.keys(state.form.parameters).forEach(key => {
    if (state.form.parameters[key].isDisabled) {
      // e.g. When order has started, start time is disabled and we don't want to validate it since it *will* be in the past
      return;
    }
    if (key === 'StartTime' && state.form.parameters.EndTime) {
      const startRules = [notInPastValidation];
      state.form.parameters.StartTime = state.form.parameters.StartTime.validate(startRules, state);
    }
    if (key === 'EndTime' && state.form.parameters.StartTime) {
      const endRules = [notInPastValidation, endTimeValidation];
      if (isClipScheduleStrategy(state.form.strategyField.value?.Name)) {
        endRules.push(clipScheduleUnderfillEndTimeValidation);
      }
      state.form.parameters.EndTime = state.form.parameters.EndTime.validate(endRules, state);
    }

    // https://talostrading.atlassian.net/browse/UI-4182
    if (key === 'ClipSize') {
      const clipRules = [numberIsPositive, clipSizeValidation, quantityValidation];
      state.form.parameters.ClipSize = state.form.parameters.ClipSize.validate(clipRules, state);
    }
    if (key === 'ClipInterval') {
      const clipIntervalRules = [clipIntervalValidation];
      state.form.parameters.ClipInterval = state.form.parameters.ClipInterval.validate(clipIntervalRules, state);
    }

    if (key === 'ShowQty') {
      state.form.parameters.ShowQty = state.form.parameters.ShowQty.validate(
        [numberIsPositive, quantityValidation],
        state
      );
    }

    if (key === 'MaxImbalanceAmt') {
      state.form.parameters.MaxImbalanceAmt = state.form.parameters.MaxImbalanceAmt.validate(
        [numberIsPositive, quantityValidation],
        {
          ...state,
          form: {
            ...state.form,
            orderCurrencyField: new Field({ value: 'USD' }),
          },
        }
      );
    }
  });
};

const orderSlice = createSlice({
  name: 'orders',
  initialState: getInitialState(),
  reducers: {
    setReferenceData: (state, action: PayloadAction<OMSReferenceDataState>) => {
      state.referenceData = action.payload;

      // handle the case where we launched the form so quickly before the app received it's first ref data tick
      if (
        state.initialized &&
        (state.form.strategyField.availableItems.length === 0 ||
          // TODO this might be dangerous as these might not exist...
          state.form.ddhStrategyField.availableItems.length === 0)
      ) {
        populateDropdownsFromRefData(state);
      }
    },
    setAdditionalDependencies: (state, action: PayloadAction<OrderAdditionalDependencies>) => {
      state.dependencies = action.payload;

      if (!state.orderBeingModified) {
        if (state.form.allocations.length && action.payload.tradableSubAccounts?.length != null) {
          const tradableSubAccountNamesSet = new Set(action.payload.tradableSubAccounts.map(sb => sb.Name));
          // Filter out sub-accounts that are no longer tradeable
          state.form.allocations = state.form.allocations.filter(a =>
            a.subAccountField.value ? tradableSubAccountNamesSet.has(a.subAccountField.value) : true
          );
        }

        validateAllocations(state);
      }
    },
    onMount: (state, action: PayloadAction) => {
      if (!state.initialized) {
        populateDropdownsFromRefData(state);
      }
      if (!state.form.allocations.length) {
        state.form.allocations = mapAllocationsToField([
          { subAccount: state.referenceData.defaultSubAccount || '', value: '1' },
        ]);
      }
      restoreLastProductGroupUsed(state);
      state.initialized = true;
    },
    modifyOrder: (state, action: PayloadAction<{ order: ExecutionReport | Order; parentType?: OrderParentType }>) => {
      state = cleanFormState(state);
      populateDropdownsFromRefData(state);

      state.orderBeingModified = action.payload.order;
      initializeFieldsFromOrder(state, action.payload.order as ExecutionReport, true, action.payload.parentType);
      state.initialized = true;
    },
    resubmitOrder: (
      state,
      action: PayloadAction<{
        order: ExecutionReport | Order | ResubmitOrderSource;
        useRemaining?: boolean;
        parentType?: OrderParentType;
      }>
    ) => {
      state = cleanFormState(state);
      populateDropdownsFromRefData(state);

      const order = action.payload.order as ExecutionReport;
      const remaining = Big(order?.OrderQty || '0').minus(order?.CumQty || '0');

      initializeFieldsFromOrder(
        state,
        {
          ...order,
          OrderQty: action.payload.useRemaining && remaining.lte(0) ? '' : order.OrderQty,
        },
        false,
        action.payload.parentType
      );

      // There are cases where the remaining qty on an order can exceed the min increment of the instrument
      // The order slice won't let you submit the order -- so here we floor the quantity to the increment so the user
      // doesn't get an annoying validation error (we can prevent it)
      if (action.payload.useRemaining && remaining.gt(0)) {
        state.form.quantityField = state.form.quantityField.updateValue(
          floorOrderQuantityToMinIncrement({
            quantity: remaining,
            security: state.form.symbolField.value,
            orderCurrency: state.form.orderCurrencyField.value,
            currenciesBySymbol: state.referenceData.currencies.currenciesBySymbol,
          })
        );
        validateQuantity(state);
      }

      validateStrategyParams(state);
      state.initialized = true;
    },
    openNewOrderForm: state => {
      if (state.form.symbolField.value != null) {
        handleSymbolChange(state, state.form.symbolField.value.Symbol);
      }
      applyCounterCurrencyDefault(state);
    },
    primeNewOrderForm: (state, action: PayloadAction<PrimeOMSParams>) => {
      if (shouldOnlyPrimePriceForRePriming(state, action.payload)) {
        // [UI-4274] - We're only going to update the price and then return early
        if (action.payload.price) {
          state.form.priceField = state.form.priceField.updateValue(action.payload.price);
        }
        if (action.payload.side) {
          state.form.sideField = state.form.sideField.updateValue(action.payload.side);
        }
        if (action.payload.predictionOutcome) {
          state.form.predictionOutcome = action.payload.predictionOutcome;
        }
        deriveSelectionIfPredictionMarket(state);
        return;
      }

      const prevSide = state.form.sideField.value;
      state = cleanFormState(state);
      populateDropdownsFromRefData(state);

      const requestedPricingMode = action.payload.pricingMode ?? PricingMode.Default;

      const symbol = action.payload.symbol;
      handleSymbolChange(state, symbol);
      // handleSymbolChange → updateLimitPriceCurrencies settles priceModeField.availableItems.
      // Only apply the requested pricing mode and price if the strategy supports it.
      const requestedPricingModeIsSupported = state.form.priceModeField.availableItems.includes(requestedPricingMode);
      if (requestedPricingModeIsSupported) {
        updatePriceModeField(state, priceModeField => priceModeField.updateValue(requestedPricingMode));
      }
      const price = requestedPricingModeIsSupported ? action.payload.price : undefined;

      if ('side' in action.payload) {
        state.form.sideField = state.form.sideField.updateValue(action.payload.side);
      } else {
        // keep the previous side.
        state.form.sideField = state.form.sideField.updateValue(prevSide ?? SideEnum.Buy);
      }

      state.form.priceField = state.form.priceField.updateValue(price);
      validatePrice(state);

      state.form.predictionOutcome = action.payload.predictionOutcome;
      setPredictionCloseMode(state, action.payload.isPredictionClose ?? false);
      deriveSelectionIfPredictionMarket(state);

      // Differentiate between priming with empty initial quantity vs using security's NormalSize
      if (action.payload.orderQty != null) {
        state.form.quantityField = state.form.quantityField
          // removes trailing zeroes after the decimal place if we toFixed() a Big object instead of just setting the rawString
          .updateValue(action.payload.orderQty && toBigWithDefault(action.payload.orderQty, 0).toFixed());
      }

      // Empty string is contracts; only skip when the caller omitted currency entirely.
      if (action.payload.currency != null) {
        state.form.orderCurrencyField = state.form.orderCurrencyField.updateValue(action.payload.currency);
      }
      validateQuantity(state);

      state.form.groupField = state.form.groupField.updateValue(action.payload.group);
      const parentID = action.payload.parent?.id;
      state.form.parentIDField = state.form.parentIDField.updateValue(parentID);
      state.form.parentType = action.payload.parent?.type;
      state.form.assignToParentField = state.form.assignToParentField.updateValue(parentID != null);

      // Anything below this line might be affected by order presets
      // (E.g. strategy, allocations, parameters, etc.)
      if (action.payload.orderPreset) {
        handleOrderPresetChange(state, action.payload.orderPreset, action.payload.primeFrom);
      } else {
        const primePayloadStrategy = state.form.strategyField.availableItems.find(
          s => s.Name === action.payload.selectedStrategy?.Name
        );

        if (primePayloadStrategy) {
          handleStrategyChange(state, primePayloadStrategy.Name);
        }

        if (action.payload.parameters?.reduceOnly && state.form.parameters.ReduceOnly) {
          state.form.parameters.ReduceOnly = state.form.parameters.ReduceOnly.updateValue(
            action.payload.parameters.reduceOnly
          );
        }
        if (action.payload.parameters?.reduceFirst && state.form.parameters.ReduceFirst) {
          state.form.parameters.ReduceFirst = state.form.parameters.ReduceFirst.updateValue(
            action.payload.parameters.reduceFirst
          );
        }

        if (action.payload.subAccountAllocations) {
          state.form.allocations = mapAllocationsToField(action.payload.subAccountAllocations);
        } else if (action.payload.subAccount) {
          state.form.allocations = mapAllocationsToField([{ subAccount: action.payload.subAccount, value: '1' }]);
        } else {
          const shouldDefaultSubAccount = !state.form.allocations.length && state.referenceData.defaultSubAccount;
          if (shouldDefaultSubAccount) {
            state.form.allocations = mapAllocationsToField([
              { subAccount: state.referenceData.defaultSubAccount!, value: '1' },
            ]);
          }
        }
        validateAllocations(state);
      }
      // End order presets

      // Unified Liquidity
      if (action.payload.parameters?.unifiedLiquidity && state.form.parameters.UnifiedLiquidity) {
        state.form.parameters.UnifiedLiquidity = state.form.parameters.UnifiedLiquidity.updateValue(
          action.payload.parameters?.unifiedLiquidity
        );

        const security = state.form.symbolField.value;
        if (isMultileg(security) && action.payload.parameters?.unifiedLiquidity === UnifiedLiquidityEnum.Enabled) {
          state.form.ulLegParamsField = LegParamsField.CreateUL(
            security,
            state.referenceData.unifiedLiquidityTokens
          ).setIsRequired(true);
        }
      }

      if (!action.payload.ignoreCounterCurrencyDefault) {
        applyCounterCurrencyDefault(state);
      }

      state.initialized = true;
    },
    createOrderPreset: (state, action: PayloadAction<IOrderPreset>) => {
      const preset = action.payload;
      state.form.orderPresetField = state.form.orderPresetField.updateAvailableItems(
        filterOrderPresets([...state.form.orderPresetField.availableItems, preset], {
          security: state.form.symbolField.value,
          strategies: state.referenceData.strategies.strategiesList,
        })
      );
      handleOrderPresetChange(state, preset);
    },
    updateOrderPreset: (state, action: PayloadAction<{ preset: IOrderPreset; select: boolean }>) => {
      const { preset, select } = action.payload;
      state.form.orderPresetField = state.form.orderPresetField.updateAvailableItems(
        filterOrderPresets(
          state.form.orderPresetField.availableItems.map(p => (p.id === preset.id ? preset : p)),
          {
            security: state.form.symbolField.value,
            strategies: state.referenceData.strategies.strategiesList,
          }
        )
      );
      if (select) {
        handleOrderPresetChange(state, preset);
      }
    },
    setSymbol: (state, action: PayloadAction<string | undefined>) => {
      handleSymbolChange(state, action.payload);
      applyCounterCurrencyDefault(state);
    },
    setSide: (state, action: PayloadAction<SideEnum>) => {
      state.form.sideField = state.form.sideField.updateValue(action.payload);
      if (state.form.strategyField.value?.Name === 'EnclaveCross') {
        state.form.quantityField = state.form.quantityField.updateValue(undefined);
      }
      validatePrice(state);
      updateAvailableCurrencies(state);
    },
    // Sets the user-selected Yes/No outcome.
    setPredictionSelection: (state, action: PayloadAction<{ outcome: ExpressedOutcomeEnum }>) => {
      state.form.predictionOutcome = action.payload.outcome;
    },
    setQuantity: (state, action: PayloadAction<string | undefined>) => {
      state.form.quantityField = state.form.quantityField.updateValue(action.payload);
      validateAllocations(state);
      validateQuantity(state);
      validateStrategyParams(state);
    },
    toggleAssignToParent: state => {
      const nextAssignToParentValue = !state.form.assignToParentField.value;
      state.form.assignToParentField = state.form.assignToParentField.updateValue(nextAssignToParentValue);

      if (nextAssignToParentValue) {
        applyCareOrderAssignmentState(state, state.currentCareOrder);
      } else {
        clearCareOrderAssignmentState(state, state.currentCareOrder);
      }

      validateAllocations(state);
      validateQuantity(state);
      validateStrategyParams(state);
    },
    setCurrentCareOrder: (state, { payload: careOrder }: PayloadAction<CareOrder | undefined>) => {
      const wasAssignedToParent = state.form.assignToParentField.value;
      state.currentCareOrder = careOrder;

      if (careOrder?.isComplete) {
        if (wasAssignedToParent) {
          clearCareOrderAssignmentState(state, careOrder);
        }
        state.form.assignToParentField = state.form.assignToParentField.updateValue(false);
      }

      if (state.form.assignToParentField.value) {
        applyCareOrderAssignmentState(state, careOrder);
      }

      validateAllocations(state);
      validateQuantity(state);
      validateStrategyParams(state);
    },
    setOrderCurrency: (state, action: PayloadAction<string>) => {
      state.form.orderCurrencyField = state.form.orderCurrencyField.updateValue(action.payload);
      state.form.quantityField = state.form.quantityField.updateValue(undefined, true);
    },
    setLimitPrice: (state, action: PayloadAction<string | undefined>) => {
      state.form.priceField = state.form.priceField.updateValue(action.payload);
      validatePrice(state);
    },
    setLimitPriceMode: (state, action: PayloadAction<PricingMode | undefined>) => {
      updatePriceModeField(state, priceModeField => priceModeField.updateValue(action.payload));
      state.form.priceField = state.form.priceField.updateValue(undefined);
      validatePrice(state);
    },
    /** Called by listener when realTimeSettings change. */
    setRealTimeSettings: (state, action: PayloadAction<OMSRealTimeSettingsState>) => {
      // Update state
      state.realTimeSettings = action.payload;

      // Side-effects: sync form fields based on new settings
      syncPricingModeWithDisplaySetting(state);
    },
    setStrategy: (state, action: PayloadAction<OrderStrategy>) => {
      state.form.orderPresetField = state.form.orderPresetField.updateValue(undefined);
      handleStrategyChange(state, action.payload?.Name);
    },
    setStrategyParams: (state, { payload }: PayloadAction<{ key: string; value?: any }>) => {
      handleStrategyParamChange(state, payload);
    },
    setOrderPreset: (state, { payload }: PayloadAction<IOrderPreset | undefined>) => {
      handleOrderPresetChange(state, payload);
    },
    setAllocations: (state, action: PayloadAction<Allocation[]>) => {
      state.form.allocations = mapAllocationsToField(action.payload, state.form.allocationValueTypeField.value, true);
      validateAllocations(state);

      const resetOrderAccountsOnSubAccountChange =
        state.referenceData.settings.enableAccountSegregation && // only do this if trading acct segregation is enabled
        state.referenceData.settings.resetOrderAccountsOnSubAccountChange;

      // we only do our resetting logic if we are just working with 1 sub account, and not several. Doing < 2 also catches the case where
      // we go from 0->1 and 1->0, since those are valid "reset" state transitions as well.
      const isSingleSubAccountChange = state.form.allocations.length < 2 && action.payload.length < 2;

      if (resetOrderAccountsOnSubAccountChange && isSingleSubAccountChange) {
        // Reset the orderMarketsField entirely (non-multileg)
        state.form.orderMarketsField = getInitialState().form.orderMarketsField;

        const security = state.form.symbolField.value;
        if (isMultileg(security)) {
          // Clear the legs. This clears items and selections.
          state.form.legParams = state.form.legParams.clearLegMarkets();
          state.form.ulLegParamsField = state.form.ulLegParamsField.clearLegMarkets();
        }
      }
    },
    setAllocationValueType: (state, action: PayloadAction<AllocationValueTypeEnum>) => {
      const mappedToNewUnit = state.form.allocations.map(pair => ({
        subAccountField: pair.subAccountField,
        valueField: pair.valueField
          .updateUnit(action.payload === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal)
          .updateValue(
            state.form.allocationValueTypeField.value === AllocationValueTypeEnum.Percentage
              ? pair.valueField.displayValue
              : pair.valueField.value
          ),
      }));
      state.form.allocations = mappedToNewUnit;
      state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(action.payload);
      validateAllocations(state);
    },
    setOrderMarkets: (
      state,
      { payload: { markets, aggregation } }: PayloadAction<{ markets: string[]; aggregation?: string }>
    ) => {
      const newValue: MarketSelectorValue = aggregation
        ? { type: 'preset', presetID: aggregation, selections: markets }
        : { type: 'custom', selections: markets };
      state.form.orderMarketsField = state.form.orderMarketsField.updateValue(newValue);
    },
    setOrderUnifiedLiquidityMarketSymbols: (
      state,
      action: PayloadAction<MarketSelectorUnifiedLiquiditySelection[]>
    ) => {
      state.form.unifiedLiquidityMarketSymbols = action.payload;
    },
    removeAnyMultipleSelectionsPerMarket: state => {
      state.form.orderMarketsField = state.form.orderMarketsField.removeAnyMultipleSelectionsPerMarket();
    },
    initializeOrderMarketsField: (
      state,
      action: PayloadAction<{
        desiredValue: MarketSelectorValue | undefined;
        items: MarketSelectorItem[];
        defaultToEmptyMarkets?: boolean;
        allowDisabledSelections?: boolean;
      }>
    ) => {
      state.form.orderMarketsField = state.form.orderMarketsField.initialize({
        desiredValue: action.payload.desiredValue,
        items: action.payload.items,
        settings: {
          defaultToEmpty:
            action.payload.defaultToEmptyMarkets ?? state.referenceData.settings.defaultToEmptyOrderMarkets,
          defaultMarketAccounts: state.referenceData.settings.defaultMarketAccounts,
          defaultAggregationId: state.referenceData.settings.defaultAggregationId,
          aggregationPresets: state.referenceData.tradingAggregationPresets,
          allowDisabledSelections: action.payload.allowDisabledSelections,
        },
      });

      // There are certain cases where the user will init the order form without being able to provide the
      // multileg/multileg market account. This is a back-stop for these cases. Whenever the symbol is a multileg symbol,
      // we initialize the orderMarketsField to have the multileg market account set, always.
      if (isMultileg(state.form.symbolField.value)) {
        state.form.orderMarketsField = state.form.orderMarketsField.updateValue({
          type: 'custom',
          selections: [MULTILEG_MARKET_ACCOUNT],
        });
      }

      if (state.form.parameters.UnifiedLiquidity?.value === UnifiedLiquidityEnum.Enabled) {
        state.form.unifiedLiquidityMarketSymbols = getOrderUnifiedLiquidityMarketSymbols(state);
      }
    },
    handleOrderMarketSelectorItemsUpdate: (state, action: PayloadAction<MarketSelectorItem[]>) => {
      state.form.orderMarketsField = state.form.orderMarketsField.updateItems(action.payload);
    },

    setComments: (state, action: PayloadAction<string | undefined>) => {
      state.form.commentsField = state.form.commentsField.updateValue(action.payload);
    },
    setStagedOrder: (state, action: PayloadAction<boolean>) => {
      state.form.stagedOrderField = state.form.stagedOrderField.updateValue(action.payload);
    },
    setGroup: (state, action: PayloadAction<string | undefined>) => {
      state.form.groupField = state.form.groupField.updateValue(action.payload);
    },
    /** Reset state to initialstate, except for reference data and dependencies. */
    resetState: state => {
      // The referenceData, dependencies, and realTimeSettings are kept since they are read-only which we update on
      // updates, not necessarily whenever the order form changes.
      // Outside the scope of this reducer, there is a difference between useOMSDependencies and useOrderDependencies
      // The former are always mounted, whereas the latter are mounted only when the order form is open and rendered.
      // realTimeSettings is also managed by useOMSDependencies, so it should persist across form open/close cycles.

      // To remove dependencies or reference data, leverage those actions instead.
      const newState = getInitialState();
      newState.referenceData = state.referenceData;
      newState.dependencies = state.dependencies;
      newState.realTimeSettings = state.realTimeSettings;
      // During the same session, auto repopulate the last used group when creating a new order
      newState.form.groupField = state.form.groupField.setTouched(false);
      restoreLastProductGroupUsed(newState);
      return newState;
    },
    unsetOrderBeingModified: state => {
      state.orderBeingModified = undefined;
    },
    touchAll: state => {
      getTypedKeys(state.form).forEach(key => {
        if (state.form[key] instanceof BaseField) {
          // @ts-expect-error - BaseField methods have trouble lining up on keyed calls
          state.form[key] = state.form[key].setTouched(true);
        }
      });

      Object.keys(state.form.parameters).forEach(param => {
        state.form.parameters[param] = state.form.parameters[param].setTouched(true);
      });

      touchAllocations(state, true);
    },
    filterProductType: (state, action: PayloadAction<ProductGroup | undefined>) => {
      applyProductGroupFilter(state, action.payload);
    },
    updateOption: (state, action: PayloadAction<Option>) => {
      state.form.option = action.payload;

      const symbol = action.payload.security?.Symbol;
      if (symbol) {
        handleSymbolChange(state, symbol);
        if (!state.orderBeingModified) {
          applyCounterCurrencyDefault(state);
        }
      } else {
        state.form.symbolField = state.form.symbolField.updateValue(undefined);
      }
    },
    updatePerp: (state, action: PayloadAction<Perp>) => {
      state.form.perp = action.payload;

      const symbol = action.payload.security?.Symbol;
      if (symbol) {
        handleSymbolChange(state, symbol);
        if (!state.orderBeingModified) {
          applyCounterCurrencyDefault(state);
        }
      } else {
        state.form.symbolField = state.form.symbolField.updateValue(undefined);
      }
    },
    updateFuture: (state, action: PayloadAction<Future>) => {
      state.form.future = action.payload;

      const symbol = action.payload.security?.Symbol;
      if (symbol) {
        handleSymbolChange(state, symbol);
        if (!state.orderBeingModified) {
          applyCounterCurrencyDefault(state);
        }
      } else {
        state.form.symbolField = state.form.symbolField.updateValue(undefined);
      }
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setMarketDataStatistics: (state, action: PayloadAction<Partial<MarketDataStatistics> | undefined>) => {
      state.marketDataStatistics = freeze(action.payload);
      validatePrice(state);
    },
    setMultilegType: (state, action: PayloadAction<StringSelectItem>) => {
      if (action.payload?.value !== state.form.multilegTypeField.value?.value) {
        state.form.symbolField = state.form.symbolField.updateValue(undefined);
      }
      state.form.multilegTypeField = state.form.multilegTypeField.updateValue(action.payload);
    },
    setDDHEnabled: (state, action: PayloadAction<boolean>) => {
      state.form.isDDHEnabled = state.form.isDDHEnabled.updateValue(action.payload);
      state.form.ddhStrategyField = state.form.ddhStrategyField.setIsRequired(action.payload);
      state.form.ddhSymbolField = state.form.ddhSymbolField.setIsRequired(action.payload);
      const ddhDefaultsByID = state.referenceData.settings.ddhDefaultsByID;
      if (state.form.isDDHEnabled.value && state.form.option && ddhDefaultsByID) {
        const id = getDDHDefaultsID(state.form.option);
        const defaults = ddhDefaultsByID.get(id);
        // Use saved defaults for selected coin and market
        if (
          !state.form.ddhSymbolField.value &&
          defaults?.symbol &&
          state.form.ddhSymbolField.availableItems.some(item => item.Symbol === defaults.symbol)
        ) {
          state.form.ddhSymbolField = state.form.ddhSymbolField.updateValue(
            state.referenceData.securities.securitiesBySymbol.get(defaults.symbol)
          );
        }
        if (
          !state.form.ddhStrategyField.value &&
          defaults?.strategy &&
          state.form.ddhStrategyField.availableItems.some(item => item.Name === defaults.strategy)
        ) {
          state.form.ddhStrategyField = state.form.ddhStrategyField.updateValue(
            state.referenceData.strategies.ddhStrategiesList.find(s => s.Name === defaults.strategy)
          );
        }
        if (!state.form.ddhAggregationField.value && defaults?.aggregation) {
          state.form.ddhAggregationField = state.form.ddhAggregationField.updateValue(defaults.aggregation);
        }
        const currentSymbol = state.form.ddhSymbolField.value?.Symbol;
        const hasULToken =
          currentSymbol != null && state.referenceData.unifiedLiquidityTokens?.get(currentSymbol) != null;
        if (!state.form.ddhUnifiedLiquidityField.value && defaults?.unifiedLiquidity && hasULToken) {
          state.form.ddhUnifiedLiquidityField = state.form.ddhUnifiedLiquidityField.updateValue(
            defaults.unifiedLiquidity
          );
        }
      }
    },
    setDDHStrategy: (state, action: PayloadAction<OrderStrategy>) => {
      const strategy = action.payload.Name && state.referenceData.strategies.strategiesByName.get(action.payload.Name);
      if (!strategy) {
        return;
      }

      state.form.ddhStrategyField = state.form.ddhStrategyField.updateValue(strategy);
    },
    setDDHSymbol: (state, action: PayloadAction<string | undefined>) => {
      const symbol = action.payload && state.referenceData.securities.securitiesBySymbol.get(action.payload);
      if (!symbol) {
        return;
      }

      state.form.ddhSymbolField = state.form.ddhSymbolField.updateValue(symbol);
      const newSymbolHasULToken = state.referenceData.unifiedLiquidityTokens?.get(symbol.Symbol) != null;
      if (!newSymbolHasULToken) {
        state.form.ddhUnifiedLiquidityField = state.form.ddhUnifiedLiquidityField.updateValue(undefined);
      }
    },
    setDDHAggregation: (state, action: PayloadAction<string | undefined>) => {
      state.form.ddhAggregationField = state.form.ddhAggregationField.updateValue(action.payload ?? '');
    },
    setDDHUnifiedLiquidity: (state, action: PayloadAction<UnifiedLiquidityEnum | undefined>) => {
      state.form.ddhUnifiedLiquidityField = state.form.ddhUnifiedLiquidityField.updateValue(action.payload);
    },
    initializeLegParams: (
      state,
      action: PayloadAction<{
        desiredSelections: string[] | undefined;
        desiredInitiating: boolean | undefined;
        desiredHaircutRatio: string | undefined;
        legIndex: number | undefined;
        items: MarketSelectorItem[];
        defaultToEmptyMarkets?: boolean;
        allowDisabledSelections?: boolean;
      }>
    ) => {
      const settings = {
        defaultToEmpty: action.payload.defaultToEmptyMarkets ?? state.referenceData.settings.defaultToEmptyOrderMarkets,
        defaultMarketAccounts: state.referenceData.settings.defaultMarketAccounts,
        allowDisabledSelections: action.payload.allowDisabledSelections,
      };

      const multilegDetails = state.form.symbolField.value?.MultilegDetails;

      if (action.payload.legIndex === undefined) {
        // UL
        state.form.ulLegParamsField = state.form.ulLegParamsField.initializeLeg({
          desiredSelections: action.payload.desiredSelections,
          multilegDetails,
          legIndex: action.payload.legIndex,
          items: action.payload.items,
          settings,
        });
      } else {
        // Non-UL
        state.form.legParams = state.form.legParams.initializeLeg({
          desiredSelections: action.payload.desiredSelections,
          legIndex: action.payload.legIndex,
          desiredInitiating: action.payload.desiredInitiating,
          desiredHaircutRatio: action.payload.desiredHaircutRatio,
          multilegDetails,
          items: action.payload.items,
          settings,
        });
      }
    },
    updateInitiatingLegs: (state, action: PayloadAction<{ value: boolean; legIndex: number }>) => {
      state.form.legParams = state.form.legParams.updateInitiatingLegs(action.payload.value, action.payload.legIndex);
    },
    updateHaircutRatio: (state, action: PayloadAction<{ value: string; legIndex: number }>) => {
      state.form.legParams = state.form.legParams.updateHaircutRatio(action.payload.value, action.payload.legIndex);
    },
    updateLegMarkets: (state, action: PayloadAction<{ value: string[]; legIndex: number }>) => {
      state.form.legParams = state.form.legParams.updateLegMarkets(action.payload.value, action.payload.legIndex);
    },
    updateUnifiedLiquidityLegMarkets: (state, action: PayloadAction<string[]>) => {
      state.form.ulLegParamsField = state.form.ulLegParamsField.updateLegMarkets(action.payload, undefined);
    },
    handleLegMarketSelectorItemsChange: (
      state,
      action: PayloadAction<{ legIndex: number | undefined; items: MarketSelectorItem[] }>
    ) => {
      if (action.payload.legIndex === undefined) {
        // unified liquidity leg
        state.form.ulLegParamsField = state.form.ulLegParamsField.updateLegItems(action.payload.items, undefined);
      } else {
        state.form.legParams = state.form.legParams.updateLegItems(action.payload.items, action.payload.legIndex);
      }
    },
  },
  // https://redux-toolkit.js.org/api/createSlice#extrareducers
  // extraReducers allows own state update in response to action generated from other slices
  // in other words, we can listen to actions from another slice (reference data) to update this slice
  // the use case for now is when the top level OMS settings is updated, we can compare the new values (payload)
  // against the current values to determine what has changed and react appropriately
  extraReducers: builder => {
    builder.addCase(setOMSSettings, (state, action) => {
      const current = state.referenceData.settings;
      if (state.orderBeingModified || !current) {
        return;
      }

      const lastProductGroupUsedChanged = current.lastProductGroupUsed !== action.payload.lastProductGroupUsed;
      state.referenceData.settings = action.payload;
      if (lastProductGroupUsedChanged) {
        restoreLastProductGroupUsed(state);
      }

      const enableDerivativeContractDefaultChanged =
        current.enableDerivativeContractDefault !== action.payload.enableDerivativeContractDefault;

      // Contract currency is modelled as empty string
      if (enableDerivativeContractDefaultChanged && state.form.orderCurrencyField.value == null) {
        setInitialOrderCcy(state);
      }

      const defaultPriceProtectionChanged = current.defaultPriceProtection !== action.payload.defaultPriceProtection;
      if (
        defaultPriceProtectionChanged &&
        state.form.parameters?.PriceProtection &&
        !state.form.parameters?.PriceProtection.isTouched
      ) {
        state.form.parameters.PriceProtection = state.form.parameters?.PriceProtection.updateValue(
          action.payload.defaultPriceProtection,
          true
        );
      }

      const showAllInPricesChanged = current.showAllInPrices !== action.payload.showAllInPrices;
      if (showAllInPricesChanged && state.form.symbolField.value && state.form.strategyField.value) {
        const ordType = getOrdType(
          action.payload.showAllInPrices,
          state.form.symbolField.value,
          state.form.strategyField.value
        );
        updatePriceFieldVisibility(state, ordType);
      }
    });
  },
});

export const selectSecuritiesForProductType = createSelector(
  (state: AppState) => state.order.referenceData.securities.securitiesList,
  (state: AppState) => state.order.productGroup,
  (state: AppState) => state.order.form.multilegTypeField.value?.value as SyntheticProductTypeEnum,
  (securities, productGroup, multilegType) => {
    return securities.filter(s => {
      if (!isNewOrderSecurity(s)) {
        return false;
      }
      const group = getGroup(s);

      if (group !== 'Multi') {
        return group === productGroup;
      } else {
        return group === productGroup && s.MultilegDetails?.SyntheticProductType === multilegType;
      }
    });
  }
);
export const selectEnableCounterCurrencySelection = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (security?: Security) => {
    return (
      security?.ProductType !== ProductTypeEnum.Basis &&
      (security?.ProductType === ProductTypeEnum.Spot || canCcyFutureOrPerp(security))
    );
  }
);

export const selectModifyingCard = createSelector(
  (state: AppState) => state.cards.openOrdersData,
  (state: AppState) => state.cards.otherOrdersData,
  (state: AppState) => state.order.orderBeingModified,
  (state: AppState) => state.cards.expandedCardID,
  (ownOrdersData, otherOrdersData, orderBeingModified, expandedCardID) => {
    if (!orderBeingModified) {
      return null;
    }
    const data = ownOrdersData.get(orderBeingModified.OrderID) || otherOrdersData.get(orderBeingModified.OrderID);
    return expandedCardID === orderBeingModified.OrderID && data ? { ...data, showDetails: true } : data;
  }
);

export const selectCurrentCareOrder = (state: AppState) => state.order.currentCareOrder;

export const selectAssignToParentToggleDisabled = createSelector(
  (state: AppState) => state.order.form.assignToParentField,
  (state: AppState) => state.order.currentCareOrder,
  (assignToParentField, currentCareOrder) => {
    if (currentCareOrder?.isComplete) {
      return true;
    }

    return assignToParentField.isDisabled;
  }
);

export const selectUseTradeAllocations = createSelector(
  (state: AppState) => state.order.referenceData.settings.useTradeAllocations,
  (state: AppState) => state.order.orderBeingModified?.Allocation,
  (state: AppState) => state.order.orderBeingModified?.SubAccount,
  (state: AppState) => state.order.dependencies.tradableSubAccounts.length,
  (useTradeAllocationsSetting, orderBeingModifiedAllocation, orderBeingModifiedSubAccount, subAccountCount) => {
    // if there is no subaccounts in system, then ignore this setting
    const useTradeAllocations = !subAccountCount ? false : useTradeAllocationsSetting;

    // When modifying an order, preserve the original structure:
    // - If order has Allocation, it must remain allocation-based (return true)
    // - If order has SubAccount (and no Allocation), it must remain SubAccount-based (return false)
    // - Otherwise, use the current setting
    if (orderBeingModifiedAllocation !== undefined) {
      return true;
    }
    if (orderBeingModifiedSubAccount !== undefined) {
      return false;
    }
    return useTradeAllocations;
  }
);

export const selectDisplayCounterAmount = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  security => {
    return (
      (isSpot(security) || isFuture(security) || isPerpetualSwap(security) || isCFD(security)) &&
      !isSecurityQuantoFuture(security)
    );
  }
);

export const selectAvailableBalance = createSelector(
  selectMarginCost,
  (state: AppState) => state.referenceData.marketAccounts.marketAccountsByName,
  (state: AppState, balancesByMarketAccountIDCurrency: Map<number, Map<string, Balance>> | undefined) =>
    balancesByMarketAccountIDCurrency,
  (
    marginForAccount: MarginCostResponse | undefined,
    marketAccountsByName: Map<string, MarketAccount>,
    balancesByMarketAccountIDCurrency: Map<number, Map<string, Balance>> | undefined
  ) => {
    if (marginForAccount && marginForAccount.Status === 'Success') {
      const marketAccount = marketAccountsByName.get(marginForAccount.MarketAccount);
      if (marketAccount && balancesByMarketAccountIDCurrency) {
        const balancesByCurrency = balancesByMarketAccountIDCurrency.get(marketAccount.MarketAccountID);
        if (balancesByCurrency) {
          const balance = balancesByCurrency.get(marginForAccount.InitialMarginCurrency!);
          return balance?.AvailableMargin;
        }
      }
    }
  }
);

export const selectIsFormValid = createSelector(
  (state: AppState) => state.order.currentCareOrder,
  (state: AppState) => state.order.form,
  (state: AppState) => state.order.dependencies.tradableSubAccounts.length,
  (state: AppState) => state.order.referenceData.marketAccounts.marketAccountsByName,
  (state: AppState) => state.order.referenceData.marketAccounts.marketAccountsByMarket,
  (state: AppState) => state.order.referenceData.settings.enableUnifiedLiquidityMarketSymbols,
  (
    currentCareOrder,
    form,
    numTradableSubAccounts,
    marketAccountsByName,
    marketAccountsByMarket,
    enableUnifiedLiquidityMarketSymbols
  ) => {
    if (
      form.parentIDField.value != null &&
      isCareOrWorkingOrderParentType(form.parentType) &&
      currentCareOrder == null
    ) {
      return false;
    }

    const hasValidParameters = parametersAreValid(form.parameters);
    const hasValidAllocations = allocationsAreValid(form.allocations, numTradableSubAccounts);
    const hasValidPrincipalFields = principalFieldsAreValid(form);
    const selectedMarketAccounts = form.orderMarketsField.selections.flatMap(selection => {
      if (marketAccountsByName.has(selection)) {
        return [selection];
      }

      return marketAccountsByMarket.get(selection)?.map(marketAccount => marketAccount.Name) ?? [];
    });
    const hasValidUnifiedLiquidityMarketSymbols =
      !enableUnifiedLiquidityMarketSymbols ||
      form.parameters.UnifiedLiquidity?.value !== UnifiedLiquidityEnum.Enabled ||
      getSelectedMarketAccountsWithoutUnifiedLiquiditySymbols({
        selectedMarketAccounts,
        marketSymbols: form.unifiedLiquidityMarketSymbols,
      }).length === 0;

    return (
      hasValidParameters && hasValidAllocations && hasValidPrincipalFields && hasValidUnifiedLiquidityMarketSymbols
    );
  }
);

export const selectAreParametersTouched = createSelector(
  (state: AppState) => state.order.form.parameters,
  parameters => {
    return Object.values(parameters).some(field => field.isTouched);
  }
);

export const selectAreAllocationsTouched = createSelector(
  (state: AppState) => state.order.form.allocations,
  allocations => {
    return allocations.some(allocation => allocation.subAccountField.isTouched || allocation.valueField.isTouched);
  }
);

export const selectCanSavePreset = createSelector(
  (state: AppState) => state.order.form,
  (state: AppState) => state.order.dependencies.tradableSubAccounts.length,
  selectAreParametersTouched,
  selectAreAllocationsTouched,
  (form, numTradableSubAccounts, areParametersTouched, areAllocationsTouched) => {
    // If a preset has already been selected, we also need to check if the form has been modified
    if (
      form.orderPresetField.value &&
      !areParametersTouched &&
      !form.strategyField.isTouched &&
      !areAllocationsTouched &&
      form.orderPresetField.value?.aggregation === form.orderMarketsField.aggregation
    ) {
      return {
        valid: false,
        message: 'Unable to save a new preset until the form has been modified.',
      };
    }

    const hasSymbolSelected = form.symbolField.value;
    const hasValidParameters = parametersAreValid(form.parameters);
    const hasValidAllocations = allocationsAreValid(form.allocations, numTradableSubAccounts);

    // When checking the so-called "principal fields" (e.g. Qty, Price, Symbol, ...) we exclude the Price field
    // Note: I expect this list to grow as we add more fields to the form
    const excludedFields = ['priceField', 'legParams', 'orderMarketsField'] satisfies (keyof OrderFormState)[];
    const hasValidPrincipalFields = principalFieldsAreValid(form, excludedFields);

    const isFormValid = hasSymbolSelected && hasValidParameters && hasValidAllocations && hasValidPrincipalFields;

    if (!isFormValid) {
      return {
        valid: false,
        message: 'Unable to save a preset until all required strategy parameters are set.',
      };
    }

    const startTime = form.parameters.StartTime as DateField | undefined;
    const endTime = form.parameters.EndTime as DateField | undefined;

    // Check that start/end times are unset, or if set, that they are durations
    const isStartTimeDateTime =
      !!startTime?.value && startTime?.dateTimeDuration?.type === DateTimeDurationPickerValueType.DateTime;
    const isEndTimeDateTime =
      !!endTime?.value && endTime?.dateTimeDuration?.type === DateTimeDurationPickerValueType.DateTime;
    if (isStartTimeDateTime || isEndTimeDateTime) {
      return {
        valid: false,
        message: 'Unable to save a preset when a specific time is set for the Start or End Time.',
      };
    }

    // Check that sub account allocations are unset, or if set, that they are percentages
    const hasPercentageAllocations = form.allocations.some(a => a.valueField.unit === Unit.Percent);
    if (form.allocations.length && !hasPercentageAllocations) {
      return {
        valid: false,
        message: 'Unable to save a preset when Sub Accounts are allocated specific amounts.',
      };
    }

    return {
      valid: true,
      message: undefined,
    };
  }
);

export const selectShouldCheckReasonability = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.strategyField.value,
  (state: AppState) => state.order.referenceData.settings.showAllInPrices,
  (state: AppState) => state.order.referenceData.settings.alwaysCheckPriceReasonability,
  (state: AppState) => state.order.marketDataStatistics?.MinOrderPrice,
  (state: AppState) => state.order.marketDataStatistics?.MaxOrderPrice,
  (security, strategy, showAllInPrices, alwaysCheckPriceReasonability, minPrice, maxPrice) => {
    const ordType = getOrdType(showAllInPrices, security, strategy);
    // Don't validate price on Market Orders as we are not sending a price
    const isMarketOrder = ordType === OrdTypeEnum.Market;

    // [ch23704] Disable price reasonability checks for some algo orders
    const isStrategyToCheck =
      strategy !== undefined && !DISABLED_REASONABILITY_CHECK_STRATEGIES.has(strategy?.Name as OrderStrategiesEnum);

    // Don't perform price reasonability checks on basis, options, or prediction outcomes
    const shouldCheckReasonability =
      !isMarketOrder &&
      shouldDisplayReasonabilityCheckForProduct(security) &&
      (isStrategyToCheck || alwaysCheckPriceReasonability);

    // [UI-2730] - Do not check reasonability when Option && Min/MaxOrderPrice is present
    if (isOption(security) && minPrice && maxPrice) {
      return false;
    }

    return !!shouldCheckReasonability;
  }
);

/** Simply grabs the Map<string, Position> map from the streaming data slice cache */
export const selectPositionsDataMap = createSelector(
  (state: AppState) => state,
  state => streamingDataSlice.endpoints.getPositions.select(ORDERSLICE_POSITION_REQUEST_PARAMS)(state).data
);

export const selectPositions = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.orderMarketsField.value,
  selectPositionsDataMap,
  (state: AppState) => state.order.form.parameters.UnifiedLiquidity,
  (state: AppState) => state.order.referenceData.unifiedLiquidityTokens,
  (state: AppState) => state.order.referenceData.marketAccounts.marketAccountsByName,
  (state: AppState) => state.order.referenceData.securities.securitiesBySymbol,
  (
    security,
    marketsValue,
    positions,
    unifiedLiquidity,
    unifiedLiquidityTokens,
    marketAccountsByName,
    securitiesBySymbol
  ) => {
    if (!security || !positions) {
      return undefined;
    }

    // Only get positions for derivatives (including prediction outcomes)
    if (
      !(
        includesEnumValue(
          [
            ProductTypeEnum.Option,
            ProductTypeEnum.Future,
            ProductTypeEnum.PerpetualSwap,
            ProductTypeEnum.PredictionOutcome,
          ],
          security.ProductType
        ) && security.Composition !== InstrumentCompositionEnum.Synthetic
      )
    ) {
      return undefined;
    }

    const marketAccountForActualSymbol = marketsValue?.selections.find(ma => {
      const marketAccount = marketAccountsByName.get(ma);
      if (!security.Markets) {
        return false;
      }
      return security.Markets?.some(m => m === marketAccount?.Market) || false;
    });

    if (!marketAccountForActualSymbol) {
      return undefined;
    }

    // Prediction outcomes and quanto futures are shown in contract terms (no currency).
    if (SecurityTypesUtils.PredictionOutcome.isProductType(security) || isSecurityQuantoFuture(security)) {
      const contractPosition =
        getOrderSlicePosition(positions, marketAccountForActualSymbol, security.Symbol)?.Amount || '0.0';
      return {
        currency: '',
        predictionOutcome: SecurityTypesUtils.PredictionOutcome.isProductType(security),
        positions: [
          {
            value: contractPosition,
            currency: '',
            marketAccount: marketAccountForActualSymbol,
            symbol: security.Symbol,
          },
        ],
      };
    }

    // Build up collection of all symbols and market accounts to get positions for
    const allSymbolAndMarketAccounts = [
      {
        symbol: security.Symbol,
        marketAccount: marketAccountForActualSymbol,
      },
    ];

    // If unified liquidity is enabled, display aggregate position for all symbols
    if (unifiedLiquidity?.value === UnifiedLiquidityEnum.Enabled) {
      const unifiedTokens = unifiedLiquidityTokens?.get(security.Symbol)?.Tokens || [];

      unifiedTokens?.forEach(t => {
        const marketAccount = marketsValue?.selections.find(m => {
          return marketAccountsByName.get(m)?.Market === t.Market;
        });
        if (!marketAccount) {
          return undefined;
        }
        allSymbolAndMarketAccounts.push({
          symbol: t.Symbol,
          marketAccount: marketAccount,
        });
      });
    }

    const allPositionAmounts = getPositionAmounts(
      allSymbolAndMarketAccounts,
      positions,
      securitiesBySymbol,
      ORDERSLICE_POSITION_REQUEST_PARAMS.Source
    );

    return {
      currency: allPositionAmounts[0]?.currency || '',
      positions: allPositionAmounts,
    };
  }
);

export const selectSupportReduceOnly = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.strategyField.value,
  (state: AppState) => state.order.form.orderMarketsField.value,
  (state: AppState) => state.order.form.parameters?.UnifiedLiquidity,
  (state: AppState) => state.order.referenceData.marketAccounts.marketAccountsByName,
  (state: AppState) => state.order.referenceData.markets.marketsByName,
  (
    security: Security | undefined,
    strategy: OrderStrategy | undefined,
    marketSelectorValue,
    unifiedLiquidity,
    marketAccountsByName,
    marketsByName
  ): boolean => {
    const isUnifiedLiquidityEnabled = unifiedLiquidity?.value === UnifiedLiquidityEnum.Enabled;
    return getReduceOnlySupport(
      security,
      strategy,
      marketSelectorValue?.selections,
      isUnifiedLiquidityEnabled,
      marketAccountsByName,
      marketsByName
    );
  }
);

export const selectSupportReduceFirst = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.parameters?.UnifiedLiquidity,
  (security: Security | undefined, unifiedLiquidity): boolean => {
    const isUnifiedLiquidityEnabled = unifiedLiquidity?.value === UnifiedLiquidityEnum.Enabled;
    return getReduceFirstSupport(security, isUnifiedLiquidityEnabled);
  }
);

export const selectIsLimitAllInOrder = createSelector(
  (state: AppState) => state.order.orderBeingModified?.OrdType,
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.strategyField.value,
  (state: AppState) => state.order.referenceData.settings.showAllInPrices,
  (orderBeingModifiedOrdType, security, strategy, showAllInPrices) => {
    const ordType = getOrdType(showAllInPrices, security, strategy);
    return orderBeingModifiedOrdType
      ? orderBeingModifiedOrdType === OrdTypeEnum.LimitAllIn
      : ordType === OrdTypeEnum.LimitAllIn;
  }
);

export const selectShouldDisplayPriceBpsButtons = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  security =>
    !isOption(security) &&
    !isDelta1Multileg(security) &&
    !isOptionStrategy(security) &&
    !SecurityTypesUtils.PredictionOutcome.isProductType(security)
);

/**
 * https://talostrading.atlassian.net/browse/UI-6704 Market data statistic does not support markets derived from unified liquidity tokens.
 * For example, if user selects BTC-USD, and enables unified liq, we need to remove binance/asian exchanges from the market data statistic sub, since they do not have BTC-USD.
 */
export const selectSymbolAndMarketsNoUnifiedLiquidity = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.orderMarketsField.value,
  (state: AppState) => state.referenceData.marketAccounts.marketAccountsByName,
  (security, marketSelectorValue, marketAccountsByName) => {
    return getSymbolAndMarketsNoUnifiedLiquidity({
      security,
      marketAccountsByName,
      marketAccountSelections: marketSelectorValue?.selections,
      marketSelections: EMPTY_ARRAY,
    });
  }
);

export const selectSymbolAndMarkets = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.orderMarketsField.value,
  (security, marketSelectorValue) => ({
    symbol: security?.Symbol,
    markets: marketSelectorValue?.selections ?? EMPTY_ARRAY,
  })
);

export const selectMinMaxOrderPrice = createSelector(
  (state: AppState) => state.order.marketDataStatistics?.MinOrderPrice,
  (state: AppState) => state.order.marketDataStatistics?.MaxOrderPrice,
  (minPrice, maxPrice) => ({ minPrice, maxPrice })
);

function parametersAreValid(parameters: Record<string, BaseField<any>>) {
  const parametersHasError = Object.keys(parameters).some(key => parameters[key].hasError);
  return !parametersHasError;
}

/**
 * Check if all principal fields are valid
 * @param form - order form state
 * @param excludedFields - fields to exclude from validation
 */
function principalFieldsAreValid(form: OrderFormState, excludedFields: (keyof OrderFormState)[] = []) {
  const principalFields: BaseField<FieldData>[] = getTypedKeys(form)
    .filter(k => !excludedFields.includes(k as keyof OrderFormState))
    .filter(k => form[k] instanceof BaseField)
    .map(k => form[k] as BaseField<FieldData>);
  const principalHasError = principalFields.some(f => f.hasError);
  return !principalHasError;
}

export const selectInitiatingLegs = createSelector(
  (state: AppState) => state.order.form.legParams.value,
  (legParams: LegParams[] | undefined): boolean[] => {
    if (!legParams) {
      return EMPTY_ARRAY;
    }
    return legParams.map(leg => leg.Initiating);
  }
);

export const selectHaircutRatios = createSelector(
  (state: AppState) => state.order.form.legParams.value,
  (legParams: LegParams[] | undefined): (string | undefined)[] => {
    if (!legParams) {
      return EMPTY_ARRAY;
    }
    return legParams.map(leg => leg.HaircutRatio);
  }
);

export const selectLegMarkets = createSelector(
  (state: AppState) => state.order.form.legParams.value,
  (legParams: LegParams[] | undefined): string[][] => {
    if (!legParams) {
      return EMPTY_ARRAY;
    }

    return legParams.map(leg => leg.Markets);
  }
);

export const selectLegMarketSelectorItems = createSelector(
  (state: AppState) => state.order.form.legParams.value,
  legParams => legParams?.map(leg => leg.items ?? null)
);

export const selectULLegMarkets = createSelector(
  (state: AppState) => state.order.form.ulLegParamsField.value,
  ulLegParams => ulLegParams?.at(0)?.Markets
);

export const selectULLegMarketSelectorItems = createSelector(
  (state: AppState) => state.order.form.ulLegParamsField.value,
  ulLegParams => ulLegParams?.at(0)?.items ?? null
);

export const selectIsULEnabled = createSelector(
  (state: AppState) => state.order.form.parameters?.UnifiedLiquidity,
  ulField => {
    return ulField?.value === UnifiedLiquidityEnum.Enabled;
  }
);

export const selectMarketDataSnapshotRequestMarketAccounts = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.orderMarketsField.selections,
  selectIsULEnabled,
  selectULLegMarkets,
  (security, orderMarketAccounts, isUnifiedLiquidityEnabled, ulLegMarkets) => {
    let snapshotRequestAccounts = [...orderMarketAccounts];

    // For multileg, if UL is enabled and the UL Leg has markets selected, we include those in the MDS request market accounts array
    if (isMultileg(security) && isUnifiedLiquidityEnabled && ulLegMarkets != null) {
      snapshotRequestAccounts = snapshotRequestAccounts.concat(ulLegMarkets);
    }

    return uniq(snapshotRequestAccounts);
  }
);

/** Given the security set on the order form and the UL tokens ref data, will return all the UL Symbols related to the selected security */
export const selectULTokenSymbols = createSelector(
  (state: AppState) => state.order.form.symbolField.value?.Symbol,
  (state: AppState) => state.referenceData.unifiedLiquidityTokens,
  (symbol, ulTokens) => {
    if (!symbol || !ulTokens) {
      return EMPTY_ARRAY;
    }

    return uniq(ulTokens.get(symbol)?.Tokens.map(token => token.Symbol));
  }
);

export const selectMSSRequestParams = createSelector(
  (state: AppState) => state.order.form.symbolField.value?.Symbol,
  selectIsULEnabled,
  selectULTokenSymbols,
  (symbol, ulEnabled, ulSymbols) => {
    if (!symbol) {
      return undefined;
    }

    const ulSymbolsToUse = ulEnabled ? ulSymbols : EMPTY_ARRAY;

    return {
      Symbols: compact(uniq([symbol, ...ulSymbolsToUse])),
    };
  }
);

const makeSelectLegMSSRequestParams = (legIndex: number) =>
  createSelector(
    [(state: AppState) => state.order.form.symbolField.value, (_: AppState) => legIndex],
    (security, legIndex) => {
      const legSymbol = security?.MultilegDetails?.Legs.at(legIndex)?.Symbol;
      if (!legSymbol) {
        return undefined;
      }

      return { Symbols: [legSymbol] };
    }
  );

const selectULLegSecurity = createSelector(
  (state: AppState) => state.order.form.ulLegParamsField.value?.at(0)?.Symbol,
  (state: AppState) => state.referenceData.securities.securitiesBySymbol,
  (symbol, securitiesBySymbol) => {
    return symbol ? securitiesBySymbol.get(symbol) : undefined;
  }
);

const selectULLegMSSRequestParams = createSelector(selectULLegSecurity, security => {
  return security ? { Symbols: [security.Symbol] } : undefined;
});

// Says if the user has selected an allocation or not
const selectAllocationsArePopulated = createSelector(
  (state: AppState) => state.order.form.allocations.at(0)?.subAccountField.value,
  allocation => {
    return allocation != null;
  }
);

const selectCanInitMarketsField = createSelector(
  selectAllocationsArePopulated,
  (state: AppState) => state.referenceData.settings.enableAccountSegregation,
  (allocationsPopulated, enableAccountSegregation) => {
    // For now just one blocker for initializing: acct segregation is enabled, then there must be a sub acct selected before we init
    if (enableAccountSegregation) {
      return allocationsPopulated;
    }

    return true;
  }
);

export const selectAllowMultipleAcctsPerCred = createSelector(
  (state: AppState) => state.referenceData.settings.disableUnifiedLiquidityDerivativesMarketSelectionRestriction,
  (state: AppState) => state.order.form.symbolField.value,
  selectIsULEnabled,
  (disableUnifiedLiquidityDerivativesMarketSelectionRestriction, security, ulEnabled) => {
    return allowMultipleAcctsPerCred({
      disableUnifiedLiquidityDerivativesMarketSelectionRestriction,
      security,
      ulEnabled,
    });
  }
);

const primeNewOrderFormThunk = createAsyncThunk<void, PrimeOMSParams, { state: AppState; extra: AppExtraArgument }>(
  'orderSlice/primeNewOrderFormThunk',
  async (params, thunk) => {
    thunk.dispatch(OMSSliceActions.hideFeedbackCard());
    thunk.dispatch(orderSlice.actions.primeNewOrderForm(params));
    const state = thunk.getState();

    // If the order markets field was reset after the dispatch directly above, we start the initialization of market selector items
    if (!state.order.form.orderMarketsField.initialized) {
      let desiredMarketSelectorValue: MarketSelectorValue | undefined = undefined;
      if (params.marketAccounts) {
        desiredMarketSelectorValue = params.aggregation
          ? { type: 'preset', presetID: params.aggregation, selections: params.marketAccounts }
          : { type: 'custom', selections: params.marketAccounts };
      }

      await thunk.dispatch(initializeOrderMarketsFieldThunk({ desiredValue: desiredMarketSelectorValue }));
    }

    if (selectIsULEnabled(state) && params.unifiedLiquidityMarketSymbols != null) {
      thunk.dispatch(orderSlice.actions.setOrderUnifiedLiquidityMarketSymbols(params.unifiedLiquidityMarketSymbols));
    }

    if (isMultileg(state.order.form.symbolField.value)) {
      thunk.dispatch(initializeAllLegParamsThunk({}));
      if (selectIsULEnabled(state)) {
        thunk.dispatch(initializeSingleLegParamsThunk({ legIndex: undefined, desiredSelections: params.ulLegMarkets }));
      }
    }
  }
);

const openNewOrderFormThunk = createAsyncThunk<void, void, { state: AppState; extra: AppExtraArgument }>(
  'orderSlice/openNewOrderFormThunk',
  (_, thunk) => {
    thunk.dispatch(OMSSliceActions.hideFeedbackCard());
    thunk.dispatch(orderSlice.actions.openNewOrderForm());

    const state = thunk.getState();
    // If the order markets field was reset after the dispatch directly above, we start the initialization of market selector items
    if (!state.order.form.orderMarketsField.initialized) {
      thunk.dispatch(initializeOrderMarketsFieldThunk({ desiredValue: undefined }));
    }

    if (isMultileg(state.order.form.symbolField.value)) {
      thunk.dispatch(initializeAllLegParamsThunk({}));

      // I don't think UL can ever be enabled right off the bat when just vanilla opening the form. Either way,
      // for future-proofing, I add the init call here
      if (selectIsULEnabled(state)) {
        thunk.dispatch(initializeSingleLegParamsThunk({ legIndex: undefined, desiredSelections: undefined }));
      }
    }
  }
);

const setSymbolThunk = createAsyncThunk<void, string | undefined, { state: AppState; extra: AppExtraArgument }>(
  'orderSlice/setSymbolThunk',
  (symbol, thunk) => {
    thunk.dispatch(orderSlice.actions.setSymbol(symbol));

    const state = thunk.getState();
    // If the order markets field was reset after the dispatch directly above, we start the initialization of market selector items
    if (!state.order.form.orderMarketsField.initialized) {
      thunk.dispatch(initializeOrderMarketsFieldThunk({ desiredValue: undefined }));
    }

    if (isMultileg(state.order.form.symbolField.value)) {
      thunk.dispatch(initializeAllLegParamsThunk({}));

      if (selectIsULEnabled(state)) {
        thunk.dispatch(initializeSingleLegParamsThunk({ legIndex: undefined, desiredSelections: undefined }));
      }
    }
  }
);

const modifyOrderThunk = createAsyncThunk<void, Order | ExecutionReport, { state: AppState; extra: AppExtraArgument }>(
  'orderSlice/modifyOrderThunk',
  async (order, thunk) => {
    thunk.dispatch(OMSSliceActions.hideFeedbackCard());
    const parentType = getOrderSliceParentType(order);
    thunk.dispatch(orderSlice.actions.modifyOrder({ order, parentType }));

    const state = thunk.getState();

    const security = state.order.form.symbolField.value;
    if (!security) {
      return;
    }

    const tradedMarketAccounts = getOrderMarketAccountsFromOrder(order, security);
    const unifiedLiquidityMarketSymbols = selectIsULEnabled(state)
      ? getOrderUnifiedLiquiditySelectionsFromOrder(order, tradedMarketAccounts, security.Symbol)
      : [];

    await thunk.dispatch(
      initializeOrderMarketsFieldThunk({
        desiredValue: { type: 'custom', selections: tradedMarketAccounts },
        allowDisabledSelections: true, // for modification, allow initializing with unselectable, but relevant, markets
      })
    );
    if (unifiedLiquidityMarketSymbols.length) {
      thunk.dispatch(orderSlice.actions.setOrderUnifiedLiquidityMarketSymbols(unifiedLiquidityMarketSymbols));
    }

    if (isMultileg(security)) {
      const { legMarketAccounts, ulLegMarketAccounts, legInitiatings, legHaircutRatios } =
        getLegMarketAccountsFromOrder(order, security);
      thunk.dispatch(
        initializeAllLegParamsThunk({
          desiredSelections: legMarketAccounts,
          desiredInitiating: legInitiatings,
          desiredHaircutRatios: legHaircutRatios,
          allowDisabledSelections: true,
        })
      );

      if (selectIsULEnabled(state)) {
        thunk.dispatch(initializeSingleLegParamsThunk({ legIndex: undefined, desiredSelections: ulLegMarketAccounts }));
      }
    }
  }
);

export const ResubmitSource = {
  PreTrade: 'preTrade',
} as const;
export type ResubmitSource = (typeof ResubmitSource)[keyof typeof ResubmitSource];

const resubmitOrderThunk = createAsyncThunk<
  void,
  { order: ExecutionReport | Order | ResubmitOrderSource; useRemaining?: boolean; source?: ResubmitSource },
  { state: AppState; extra: AppExtraArgument }
>('orderSlice/resubmitOrderThunk', async (params, thunk) => {
  thunk.dispatch(OMSSliceActions.hideFeedbackCard());
  const parentType = getOrderSliceParentType(params.order);
  thunk.dispatch(orderSlice.actions.resubmitOrder({ ...params, parentType }));
  if (params.source !== ResubmitSource.PreTrade) {
    thunk.dispatch(suspendTracking());
  }
  const state = thunk.getState();
  const security = state.order.form.symbolField.value;
  if (!security) {
    return;
  }

  const tradedMarketAccounts = getOrderMarketAccountsFromOrder(params.order as ExecutionReport, security);
  const unifiedLiquidityMarketSymbols = selectIsULEnabled(state)
    ? getOrderUnifiedLiquiditySelectionsFromOrder(params.order, tradedMarketAccounts, security.Symbol)
    : [];

  await thunk.dispatch(
    initializeOrderMarketsFieldThunk({
      desiredValue: { type: 'custom', selections: tradedMarketAccounts },
      allowDisabledSelections: true, // for resubmission, allow initializing with unselectable, but relevant, markets
    })
  );
  if (unifiedLiquidityMarketSymbols.length) {
    thunk.dispatch(orderSlice.actions.setOrderUnifiedLiquidityMarketSymbols(unifiedLiquidityMarketSymbols));
  }

  if (isMultileg(security)) {
    const { legMarketAccounts, ulLegMarketAccounts, legInitiatings, legHaircutRatios } = getLegMarketAccountsFromOrder(
      params.order as ExecutionReport,
      security
    );

    thunk.dispatch(
      initializeAllLegParamsThunk({
        desiredSelections: legMarketAccounts,
        desiredInitiating: legInitiatings,
        desiredHaircutRatios: legHaircutRatios,
        allowDisabledSelections: true,
      })
    );

    if (selectIsULEnabled(state)) {
      thunk.dispatch(initializeSingleLegParamsThunk({ legIndex: undefined, desiredSelections: ulLegMarketAccounts }));
    }
  }
});

const setAllocationsThunk = createAsyncThunk<void, Allocation[], { state: AppState; extra: AppExtraArgument }>(
  'orderSlice/setAllocationsThunk',
  (params, thunk) => {
    thunk.dispatch(orderSlice.actions.setAllocations(params));

    const state = thunk.getState();
    if (!state.order.form.orderMarketsField.initialized) {
      // If the setAllocations action cleared the orderMarketsField, then we kick off a initialization thunk here
      thunk.dispatch(
        // Override default to empty to be false for this action
        initializeOrderMarketsFieldThunk({ desiredValue: undefined, defaultToEmptyMarkets: false })
      );
    }

    if (
      isMultileg(state.order.form.symbolField.value) &&
      !state.order.form.legParams.initialized // if the setAllocations dispatch cleared the legParams, re-init here
    ) {
      thunk.dispatch(initializeAllLegParamsThunk({ defaultToEmptyMarkets: false }));

      if (selectIsULEnabled(state)) {
        thunk.dispatch(
          initializeSingleLegParamsThunk({
            legIndex: undefined,
            desiredSelections: undefined,
            defaultToEmptyMarkets: false,
          })
        );
      }
    }
  }
);

const setStrategyParamsThunk = createAsyncThunk<
  void,
  { key: string; value?: any },
  { state: AppState; extra: AppExtraArgument }
>('orderSlice/setStrategyParamsThunk', async (params, thunk) => {
  thunk.dispatch(orderSlice.actions.setStrategyParams(params));

  const shouldRemoveMultipleAccts =
    !selectAllowMultipleAcctsPerCred(thunk.getState()) &&
    thunk.getState().order.form.orderMarketsField.hasMultipleSelectionsPerMarket();
  if (shouldRemoveMultipleAccts) {
    // If the user has selected multiple accts per market, and thats not allowed anymore after a strategy param update (currently UL), then go thru the motions to clear these selections out
    // We need to grab the most recent state of items in order to decide which selection to remove within any market for better UX
    const items = unwrapResult(
      await thunk.dispatch(
        getMarketSelectorItemsThunk({ selectMSSRequestParams, buildItems: buildMarketSelectorItems })
      )
    );

    if (items) {
      thunk.dispatch(orderSlice.actions.handleOrderMarketSelectorItemsUpdate(items));
      thunk.dispatch(orderSlice.actions.removeAnyMultipleSelectionsPerMarket());
    }
  }

  // If after the strategy parameter changing, we have ended up with an un-initialized ulLegParamsField, we here
  // call for it to be initialized
  if (
    isMultileg(thunk.getState().order.form.symbolField.value) &&
    !thunk.getState().order.form.ulLegParamsField.initialized
  ) {
    thunk.dispatch(initializeSingleLegParamsThunk({ legIndex: undefined, desiredSelections: undefined }));
  }
});

/**
 * A thunk wrapping the setOrderPreset action. The purpose of the thunk wrapper is to re-initialize the orderMarketsField
 * for the case where an aggregation is passed. In that case, the orderMarketsField should be reset to the value corresponding to the
 * preset's aggregation.
 *
 * For now, aggregations are just passed in to the thunk as a param here. In the future, when aggregations are moved into rtk-query, we can easily
 * just include the getting of the aggregations as a part of this thunk.
 */
const setOrderPresetThunk = createAsyncThunk<
  void,
  { preset: IOrderPreset | undefined; aggregations: MarketSelectorPreset[] | undefined },
  { state: AppState; extra: AppExtraArgument }
>('orderSlice/setOrderPresetThunk', (params, thunk) => {
  thunk.dispatch(orderSlice.actions.setOrderPreset(params.preset));

  // The setOrderPreset action will reset the orderMarketsField if an aggregation is provided. If that happens, we will do the below.
  if (!thunk.getState().order.form.orderMarketsField.initialized && params.aggregations) {
    const aggregation = params.aggregations.find(agg => agg.id === params.preset?.aggregation);
    if (aggregation) {
      thunk.dispatch(
        initializeOrderMarketsFieldThunk({
          desiredValue: { type: 'preset', presetID: aggregation.id, selections: aggregation.itemIDs },
        })
      );
    } else {
      // The user provided an aggregation, but we are unable to resolve it for some reason. In this case, we default to empty markets to avoid making unintended selections.
      thunk.dispatch(initializeOrderMarketsFieldThunk({ desiredValue: { type: 'custom', selections: [] } }));
    }
  }
});

const updatePerpThunk = createAsyncThunk<void, Perp, { state: AppState; extra: AppExtraArgument }>(
  'orderSlice/updatePerpThunk',
  (perp, thunk) => {
    thunk.dispatch(orderSlice.actions.updatePerp(perp));
    if (!thunk.getState().order.form.orderMarketsField.initialized) {
      thunk.dispatch(initializeOrderMarketsFieldThunk({ desiredValue: undefined }));
    }
  }
);

const updateFutureThunk = createAsyncThunk<void, Future, { state: AppState; extra: AppExtraArgument }>(
  'orderSlice/updateFutureThunk',
  (future, thunk) => {
    thunk.dispatch(orderSlice.actions.updateFuture(future));
    if (!thunk.getState().order.form.orderMarketsField.initialized) {
      thunk.dispatch(initializeOrderMarketsFieldThunk({ desiredValue: undefined }));
    }
  }
);

const updateOptionThunk = createAsyncThunk<void, Option, { state: AppState; extra: AppExtraArgument }>(
  'orderSlice/updateOptionThunk',
  (option, thunk) => {
    thunk.dispatch(orderSlice.actions.updateOption(option));
    if (!thunk.getState().order.form.orderMarketsField.initialized) {
      thunk.dispatch(initializeOrderMarketsFieldThunk({ desiredValue: undefined }));
    }
  }
);
export const orderSliceActions = {
  primeNewOrderForm: primeNewOrderFormThunk,
  openNewOrderForm: openNewOrderFormThunk,
  setSymbol: setSymbolThunk,
  modifyOrder: modifyOrderThunk,
  resubmitOrder: resubmitOrderThunk,
  setAllocations: setAllocationsThunk,
  setStrategyParams: setStrategyParamsThunk,
  setOrderPreset: setOrderPresetThunk,
  updatePerp: updatePerpThunk,
  updateFuture: updateFutureThunk,
  updateOption: updateOptionThunk,
  setReferenceData: orderSlice.actions.setReferenceData,
  setAdditionalDependencies: orderSlice.actions.setAdditionalDependencies,
  setSide: orderSlice.actions.setSide,
  setPredictionSelection: orderSlice.actions.setPredictionSelection,
  setQuantity: orderSlice.actions.setQuantity,
  toggleAssignToParent: orderSlice.actions.toggleAssignToParent,
  setCurrentCareOrder: orderSlice.actions.setCurrentCareOrder,
  setOrderCurrency: orderSlice.actions.setOrderCurrency,
  setLimitPrice: orderSlice.actions.setLimitPrice,
  setLimitPriceMode: orderSlice.actions.setLimitPriceMode,
  setRealTimeSettings: orderSlice.actions.setRealTimeSettings,
  setStrategy: orderSlice.actions.setStrategy,
  setAllocationValueType: orderSlice.actions.setAllocationValueType,
  setOrderMarkets: orderSlice.actions.setOrderMarkets,
  setOrderUnifiedLiquidityMarketSymbols: orderSlice.actions.setOrderUnifiedLiquidityMarketSymbols,
  handleLegMarketSelectorItemsChange: orderSlice.actions.handleLegMarketSelectorItemsChange,
  setComments: orderSlice.actions.setComments,
  setStagedOrder: orderSlice.actions.setStagedOrder,
  setGroup: orderSlice.actions.setGroup,
  onMount: orderSlice.actions.onMount,
  unsetOrderBeingModified: orderSlice.actions.unsetOrderBeingModified,
  resetState: orderSlice.actions.resetState,
  touchAll: orderSlice.actions.touchAll,
  filterProductType: orderSlice.actions.filterProductType,
  updateInitiatingLegs: orderSlice.actions.updateInitiatingLegs,
  updateHaircutRatio: orderSlice.actions.updateHaircutRatio,
  updateLegMarkets: orderSlice.actions.updateLegMarkets,
  updateUnifiedLiquidityLegMarkets: orderSlice.actions.updateUnifiedLiquidityLegMarkets,
  setIsLoading: orderSlice.actions.setIsLoading,
  setMarketDataStatistics: orderSlice.actions.setMarketDataStatistics,
  setDDHEnabled: orderSlice.actions.setDDHEnabled,
  setDDHStrategy: orderSlice.actions.setDDHStrategy,
  setDDHSymbol: orderSlice.actions.setDDHSymbol,
  setDDHAggregation: orderSlice.actions.setDDHAggregation,
  setDDHUnifiedLiquidity: orderSlice.actions.setDDHUnifiedLiquidity,
  setMultilegType: orderSlice.actions.setMultilegType,
  createOrderPreset: orderSlice.actions.createOrderPreset,
  updateOrderPreset: orderSlice.actions.updateOrderPreset,
};

const initializeOrderMarketsFieldThunk = getInitMarketsThunk({
  sliceID: 'orderSlice',
  selectCanInit: selectCanInitMarketsField,
  selectMSSRequestParams,
  buildItems: buildMarketSelectorItems,
  initMarketsField: orderSlice.actions.initializeOrderMarketsField,
});

/**
 * This thunk kicks off the process of initializing each standard (non-UL) leg.
 * It takes in desired configurations for selections and initiating, and will use this to initialize each leg.
 * The individual legs are initialized async independently of each other.
 */
const initializeAllLegParamsThunk = createAsyncThunk<
  void,
  {
    desiredSelections?: (string[] | undefined)[];
    desiredInitiating?: boolean[];
    desiredHaircutRatios?: string[];
    defaultToEmptyMarkets?: boolean;
    allowDisabledSelections?: boolean;
  },
  { state: AppState; extra: AppExtraArgument }
>('orderSlice/initializeLegParamsThunk', (params, thunk) => {
  const state = thunk.getState();

  const legs = state.order.form.legParams.value;
  if (!legs || legs.length === 0) {
    // shouldn't happen
    return;
  }

  legs.forEach(leg => {
    if (leg.LegIndex != null) {
      thunk.dispatch(
        initializeSingleLegParamsThunk({
          desiredSelections: params.desiredSelections?.at(leg.LegIndex),
          desiredInitiating: params.desiredInitiating?.at(leg.LegIndex),
          desiredHaircutRatio: params.desiredHaircutRatios?.at(leg.LegIndex),
          legIndex: leg.LegIndex,
          defaultToEmptyMarkets: params.defaultToEmptyMarkets,
          allowDisabledSelections: params.allowDisabledSelections,
        })
      );
    }
  });
});

/**
 * Used to initialize a single leg. Provide a legIndex 0 or 1 to initialize a normal leg, or legIndex: undefined
 * to initialize the UL leg.
 */
const initializeSingleLegParamsThunk = createAsyncThunk<
  void,
  {
    desiredSelections: string[] | undefined;
    desiredInitiating?: boolean;
    desiredHaircutRatio?: string;
    multilegDetails?: Security['MultilegDetails'];
    /** legIndex being undefined means UL */
    legIndex: number | undefined;
    defaultToEmptyMarkets?: boolean;
    allowDisabledSelections?: boolean;
  },
  { state: AppState; extra: AppExtraArgument }
>('orderSlice/initializeLegParamsThunk', async (params, thunk) => {
  if (!selectCanInitMarketsField(thunk.getState())) {
    return;
  }

  const mssRequestParams =
    params.legIndex === undefined
      ? selectULLegMSSRequestParams(thunk.getState())
      : makeSelectLegMSSRequestParams(params.legIndex)(thunk.getState());
  if (!mssRequestParams) {
    logger.warn('initializeSingleLegParamsThunk ran without being able to generate mssRequestParams');
    return;
  }

  const mssAction = await thunk.dispatch(getMarketSecurityStatusesThunk({ requestParams: mssRequestParams }));
  const mss = unwrapResult(mssAction);
  const items = buildLegMarketSelectorItems(thunk.getState(), { mss }, params.legIndex);

  thunk.dispatch(
    orderSlice.actions.initializeLegParams({
      desiredSelections: params.desiredSelections,
      desiredInitiating: params.desiredInitiating,
      desiredHaircutRatio: params.desiredHaircutRatio,
      legIndex: params.legIndex,
      items,
      defaultToEmptyMarkets: params.defaultToEmptyMarkets,
      allowDisabledSelections: params.allowDisabledSelections,
    })
  );
});

function buildMarketSelectorItems(
  state: AppState,
  onDemandState: { mss: Map<string, Map<string, MarketSecurityStatus>> | undefined }
): MarketSelectorItem[] {
  const params = selectBuildOrderMarketSelectorItemsParams(state);
  const items = getOrderMarketSelectorItems({
    ...params,
    mssByAccountBySymbol: onDemandState.mss,
    enableMSSValidation: state.referenceData.settings.enableMSSValidation,
  });

  return items;
}

function buildLegMarketSelectorItems(
  state: AppState,
  onDemandState: { mss: Map<string, Map<string, MarketSecurityStatus>> | undefined },
  legIndex: number | undefined
) {
  const params = makeSelectBuildOrderMarketSelectorItemsParams(legIndex)(state);
  const items = getOrderMarketSelectorItems({
    ...params,
    mssByAccountBySymbol: onDemandState.mss,
    enableMSSValidation: state.referenceData.settings.enableMSSValidation,
  });

  return items;
}

/**
 * Sets up the listeners needed by the OrderSlice.
 * The bulk of the listener is listeners for market selector item changes. These listeners are started up when initialization is done.
 * There is one listener for the order markets, and then one listener for each of the 3 possible legs (0, 1, UL).
 */
export function setupListeners(startListening: AppStateListenerStart) {
  // Sync real-time settings to order form when they change
  const unsubscribeRealTimeSettingsListener = startListening({
    actionCreator: setRealTimeOMSSettings,
    effect: (action, listener) => {
      listener.dispatch(orderSlice.actions.setRealTimeSettings(action.payload));
    },
  });

  const unsubscribeCareOrderForOrder = startCareOrderParentListener({
    startListening,
    matcher: action =>
      // These are all the entry points into the order slice that can carry a care order connotation, meaning that this listener needs to get going
      orderSlice.actions.primeNewOrderForm.match(action) ||
      orderSlice.actions.modifyOrder.match(action) ||
      orderSlice.actions.resubmitOrder.match(action),
    openedView: OMSView.NewOrderForm,
    selectParentID: state => state.order.form.parentIDField.value,
    selectParentType: state => state.order.form.parentType,
    setCurrentCareOrder: orderSlice.actions.setCurrentCareOrder,
  });

  const unsubscribeModifyOrderCardDataListener = startModifyOrderCardDataListener(startListening);

  const unsubscribeOrderMarketSelectorItemsListener = startListening(
    getMarketSelectorItemsListener({
      id: 'orderSlice/order',
      predicate: action => initializeOrderMarketsFieldThunk.fulfilled.match(action),
      selectMSSRequestParams,
      startListening,
      selectItemBuildingParams: selectBuildOrderMarketSelectorItemsParams,
      buildItems: buildMarketSelectorItems,
      onItemChange: (items, dispatch) => dispatch(orderSlice.actions.handleOrderMarketSelectorItemsUpdate(items)),
      OMSView: OMSView.NewOrderForm,
    })
  );

  const unsubscribeLeg0MarketSelectorItemsListener = startListening(
    getMarketSelectorItemsListener({
      id: 'orderSlice/leg0',
      legIndex: 0,
      predicate: action => initializeSingleLegParamsThunk.fulfilled.match(action) && action.meta.arg.legIndex === 0,
      selectMSSRequestParams: makeSelectLegMSSRequestParams(0),
      startListening,
      selectItemBuildingParams: makeSelectBuildOrderMarketSelectorItemsParams(0),
      buildItems: buildLegMarketSelectorItems,
      onItemChange: (items, dispatch) =>
        dispatch(orderSlice.actions.handleLegMarketSelectorItemsChange({ items, legIndex: 0 })),
      OMSView: OMSView.NewOrderForm,
    })
  );

  const unsubscribeLeg1MarketSelectorItemsListener = startListening(
    getMarketSelectorItemsListener({
      id: 'orderSlice/leg1',
      legIndex: 1,
      predicate: action => initializeSingleLegParamsThunk.fulfilled.match(action) && action.meta.arg.legIndex === 1,
      selectMSSRequestParams: makeSelectLegMSSRequestParams(1),
      startListening,
      selectItemBuildingParams: makeSelectBuildOrderMarketSelectorItemsParams(1),
      buildItems: buildLegMarketSelectorItems,
      onItemChange: (items, dispatch) =>
        dispatch(orderSlice.actions.handleLegMarketSelectorItemsChange({ items, legIndex: 1 })),
      OMSView: OMSView.NewOrderForm,
    })
  );

  const unsubscribeULLegMarketSelectorItemsListener = startListening(
    getMarketSelectorItemsListener({
      id: 'orderSlice/ulLeg',
      legIndex: undefined,
      predicate: action =>
        initializeSingleLegParamsThunk.fulfilled.match(action) && action.meta.arg.legIndex === undefined,
      selectMSSRequestParams: selectULLegMSSRequestParams,
      startListening,
      selectItemBuildingParams: makeSelectBuildOrderMarketSelectorItemsParams(undefined),
      buildItems: buildLegMarketSelectorItems,
      onItemChange: (items, dispatch) =>
        dispatch(orderSlice.actions.handleLegMarketSelectorItemsChange({ items, legIndex: undefined })),
      OMSView: OMSView.NewOrderForm,
    })
  );

  const debouncedGetMarginCost = debounce(
    (dispatch: ThunkDispatch<AppState, AppExtraArgument, UnknownAction>, params: MarginCostRequestParams) => {
      dispatch(orderSliceApi.endpoints.getMarginCost.initiate(params));
    },
    500,
    // leading: false. do not kick off the request until the flurry of events have come thru for better ux.
    { leading: false, trailing: true }
  );

  // Whenever the margin cost request to be made changes, make that new request.
  const unsubscribeRequestMarginCostListener = startListening({
    predicate: (_, next, prev) => {
      return !isEqual(selectMarginCostRequestParams(prev), selectMarginCostRequestParams(next));
    },
    effect: (_, listener) => {
      const requestParams = selectMarginCostRequestParams(listener.getState());
      if (requestParams == null) {
        return;
      }

      debouncedGetMarginCost(listener.dispatch, requestParams);
    },
  });

  const unsubscribePositionSubListener = startListening({
    predicate: (_, next, prev) => {
      // We run the effect if the OMS was just opened to NewOrderForm.
      return prev.OMS.openedView !== next.OMS.openedView && next.OMS.openedView === OMSView.NewOrderForm;
    },
    effect: async (_, listener) => {
      /**
       * This effect will kick off the Positions subscription which is later used in various OrderSlice selectors upon opening the NewOrderForm.
       * The listener will close the subscription when the user leaves the NewOrderForm.
       */
      listener.cancelActiveListeners();
      const positionSub = listener.dispatch(
        streamingDataSlice.endpoints.getPositions.initiate(ORDERSLICE_POSITION_REQUEST_PARAMS)
      );

      listener.signal.addEventListener('abort', () => {
        positionSub.unsubscribe();
      });

      // Keep the listener alive until the order form is closed.
      await listener.condition(() => {
        return listener.getState().OMS.openedView !== OMSView.NewOrderForm;
      });
    },
  });

  return () => {
    unsubscribeRealTimeSettingsListener();
    unsubscribeCareOrderForOrder();
    unsubscribeModifyOrderCardDataListener();
    unsubscribeOrderMarketSelectorItemsListener();
    unsubscribeLeg0MarketSelectorItemsListener();
    unsubscribeLeg1MarketSelectorItemsListener();
    unsubscribeULLegMarketSelectorItemsListener();
    unsubscribeRequestMarginCostListener();
    unsubscribePositionSubListener();
  };
}

export const orderSliceReducer = orderSlice.reducer;
